import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import type { AssignedProduct } from '@discovr/types';
import { useCampaignStabParam } from '../../../campaign-detail/useCampaignStabParam';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ScriptDoc } from '@discovr/types';
import { ScriptDocRenderer } from '../../../campaign-detail/scriptDoc/ScriptDocRenderer';
import { plainTextFromDoc } from '../../../campaign-detail/scriptDoc/docText';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { SimpleTooltip } from '../../ui/tooltip';
import { LinkedProductsBar } from '../LinkedProductsBar';
import { cn } from '../../../lib/utils';
import { Info as DsInfo, Star as DsStar, Sparkle as DsSparkle } from '../../../design-system/icons';
import {
  Warning,
  CheckCircle,
  Circle,
  CaretDown,
  CaretUp,
  FileText,
  Plus,
  ArrowsClockwise,
  ArrowsCounterClockwise,
  Check,
  ChatCircleText,
  Sparkle,
  XCircle,
} from '@phosphor-icons/react';
import { aspectTone, severityTone, VARIANT_INSUFFICIENT_RANGE_SENTINEL, type GeoAiEnvelope, type GeoPack, type LineSeverity, type ScriptReviewResult, type ScoreBreakdown } from './types';
import { ManualReviewBanner } from './parts/ManualReviewBanner';
import { VariantHeaderCard } from './parts/VariantHeaderCard';
import { BriefCoverageTable } from './parts/BriefCoverageTable';
import { ScriptTranslateToggle } from './parts/ScriptTranslateToggle';
import { useScriptTranslation } from './hooks/useScriptTranslation';
import { useFlashHighlight } from './hooks/useFlashHighlight';
import {
  applyAnchorHighlights,
  clearAnchorHighlights,
  findAnchorRange,
  SCRIPT_HIGHLIGHT_FOCUS_NAME,
  SCRIPT_HIGHLIGHT_OPEN_LAYERS,
  scriptOpenHighlightLayerName,
  type HighlightableAnchor,
  type ResolvedAnchorHighlight,
} from './highlightAnchors';
import {
  ScriptTranslationProvider,
  useScriptTranslationContext,
} from './context/ScriptTranslationContext';
import geoAgentAvatar from '../../../assets/geo-agent.png';

type TabKey = 'script' | 'overview' | 'lineby' | 'variants' | 'safety' | 'geo';

/** What a Line-by-Line "Add to comment" click hands the wrapping panel:
 *  the row's original text (the anchor quote) + its aspect tag. */
export type AddToCommentPayload = {
  originalText: string;
  aspect?: string;
  /** The AI row's own "Issue identified" / "Suggested edit" text — carried
   *  through so the wrapping panel can pre-fill the chat draft with them,
   *  instead of leaving the composer empty after staging the anchor. */
  issue?: string;
  suggestion?: string;
};

/** What a Script-tab text selection hands the wrapping panel: the
 *  selected rendered text + which occurrence of it (0-based) so
 *  duplicate spans disambiguate. */
export type SelectCommentPayload = { quotedText: string; occurrence: number };

// "Overview" sits FIRST to match the Script Review Figma (1672-30543)
// and the Content Review panel — the AI verdict/scores are the landing
// surface; the raw markdown is the "Script" tab one over.
const TABS: ReadonlyArray<{ id: TabKey; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'script', label: 'Script' },
  { id: 'lineby', label: 'Line-by-Line' },
  { id: 'variants', label: 'Platform Variants' },
  { id: 'safety', label: 'Safety & Compliance' },
];

// The GEO tab is appended only for YouTube campaigns (showGeo). It has its own
// data + status (the geo_analysis envelope), independent of the script-review AI.
const GEO_TAB: { id: TabKey; label: string } = { id: 'geo', label: 'GEO Optimized' };

// One ::highlight() rule per open-comment layer (see
// SCRIPT_HIGHLIGHT_OPEN_LAYERS) — same color on every layer; it's stacking
// independent layers, not the color itself, that makes overlapping open
// comments read darker. Static across the module, not per-render.
const OPEN_HIGHLIGHT_CSS = Array.from(
  { length: SCRIPT_HIGHLIGHT_OPEN_LAYERS },
  (_, i) => `::highlight(${scriptOpenHighlightLayerName(i)}) { background-color: rgba(108, 9, 247, 0.16); }`,
).join('\n');

/** A quote to scroll to / flash-highlight in the rendered script — text
 *  ALONE isn't enough to disambiguate which span, the same way an anchor's
 *  persistent purple highlight needs `occurrence` (see highlightAnchors.ts /
 *  MessageAnchor.occurrence). Every setter below constructs a fresh object
 *  literal rather than reusing/memoizing one, deliberately: object identity
 *  (not content equality) is what makes a repeat trigger on the SAME quote
 *  still retrigger the scroll+flash effects downstream, since React would
 *  otherwise bail on a same-value primitive update. */
type ScriptQuoteTarget = { text: string; occurrence: number };

export type ScriptResultTabsProps = {
  /** AI analysis result (unwrapped). Only consumed by the AI-driven
   *  tabs (Overview / Line-by-Line / Platform Variants / Safety). The
   *  Script tab renders independent of this — it just needs the
   *  markdown body. */
  data?: ScriptReviewResult;
  /** AI analysis status — drives whether the AI tabs render their
   *  content vs. a "running…" / "failed" placeholder. */
  aiStatus?: 'pending' | 'complete' | 'failed';
  /** Optional error message shown when aiStatus === 'failed'. */
  aiError?: string;
  /** No production caller populates this anymore — every script now ships
   *  `doc`, and `ScriptDetail.text`/`PublicScriptView.markdown` (the wire
   *  fields this was fed from) were removed once the doc-JSON backfill
   *  completed. Kept only as `useScriptTranslation`'s internal fallback and
   *  a defensive render path for a hypothetical doc-less script; not wired
   *  to any real data source. */
  scriptText?: string;
  /** The script's native document (Lexical), rendered read-only on the
   *  "Script" tab. Always present in practice. */
  doc?: ScriptDoc;
  /** The script text's own fetch is in flight — an empty scriptText while
   *  this is true renders a loading skeleton, not the "No script text yet"
   *  empty state (being on script review implies a script exists). */
  scriptTextLoading?: boolean;
  /**
   * When omitted, the per-row "Add to Comment" buttons are hidden.
   * Fired with the row's ORIGINAL text + aspect (not the suggested edit) —
   * the wrapping panel stages it as a quote-reply anchor above the chat
   * composer; the suggestion stays visible in the row itself.
   */
  onAddComment?: (payload: AddToCommentPayload) => void | Promise<void>;
  /** Select-to-comment on the Script tab (PRD §4.2). Fired when the user
   *  selects text and clicks the floating Comment button; gated by
   *  `canAddLineComment` like the per-row button. */
  onSelectComment?: (payload: SelectCommentPayload) => void;
  /** Open-comment anchors painted as persistent highlights over the
   *  Script tab's rendered text. */
  highlightAnchors?: HighlightableAnchor[];
  /** Hovering a highlighted (already-commented) span surfaces a "View
   *  comment" pill; clicking it fires this with that anchor's message id
   *  so the wrapping panel can scroll/flash it in chat — the reverse
   *  direction of onJumpToAnchor (chat → script). Omit to suppress the
   *  hover affordance entirely (e.g. no chat pane to jump to). */
  onViewComment?: (messageId: string) => void;
  /** Script version archive (newest first) for the Script tab dropdown. */
  versions?: Array<{ id: string; version: number; current?: boolean }>;
  /** Fetches an archived revision's markdown for read-only viewing. */
  onFetchVersionText?: (versionId: string) => Promise<string>;
  /** Gates the per-row "Add to Comment" button separately from `onAddComment`
   *  being wired at all — pass `false` once the script has moved past the
   *  script-review phase (e.g. status is now `content_pending`) so a stale
   *  line suggestion can't be appended to a review that's already over.
   *  Defaults to `true` (matches the pre-existing behavior). */
  canAddLineComment?: boolean;
  /** External one-shot scroll target — e.g. clicking an anchored quote in
   *  the chat thread jumps here, same as an AI row's "Comment" click does
   *  internally. Switches to the Script tab and highlights the line.
   *  `nonce` is what makes a click on the SAME quote as last time still
   *  retrigger — a bare string can't tell "not consumed yet" apart from
   *  "same text as the jump I already consumed". */
  jumpToQuote?: (ScriptQuoteTarget & { nonce: number }) | null;
  onJumpedToQuote?: () => void;
  /** GEO optimizer pack envelope (from ScriptDetail.geo_analysis). When
   *  showGeo is true a "GEO Optimized" tab is shown; it renders this pack with
   *  its own pending/complete/failed states (independent of the script-review AI). */
  geoEnvelope?: GeoAiEnvelope;
  /** Show the GEO Optimized tab. Gate to YouTube campaigns at the call site. */
  showGeo?: boolean;
  /** Trigger GEO generation (POST /roster/{id}/geo/generate). When provided, a
   *  "Generate pack" CTA is shown if no pack exists yet. */
  onGenerateGeo?: () => void | Promise<void>;
  /** True while a GEO generation request is in flight (disables the CTA). */
  geoGenerating?: boolean;
  /** Regenerate a GEO element the brand didn't like — element one of
   *  titles/description/pinned_comment/on_screen_cues, or '' for the whole pack
   *  (POST /roster/{id}/geo/regenerate). Resolves once the fresh pack is fetched. */
  onRegenerateGeo?: (element: string) => void | Promise<void>;
  /** Auto-save the approved GEO per-element selection on each toggle
   *  (POST /roster/{id}/geo/selection). */
  onSaveGeoSelection?: (selected: string[]) => void | Promise<void>;
  /** Read-only GEO surface (Confirmed/finalized tab). When true the GEO tab
   *  renders the approved pack with the approved selection highlighted but no
   *  editing controls (no auto-generate, no Select toggles, no Regenerate). */
  geoReadOnly?: boolean;
  /** Products assigned to this creator (Multi-Product Campaigns) — drives the
   *  "Link Products" row rendered below the tab bar. */
  assignedProducts?: AssignedProduct[];
  /** Campaign product-catalog size — gates the Link Products row to >= 2. */
  campaignProductCount?: number;
};

/**
 * Some upstream payloads deliver the result wrapped in an envelope shape
 * `{ analysis_id, result, roster_entry_id, computed_at }` (e.g. the brand/admin
 * GET /api/v1/brand/roster/{id}/script response). Others deliver the result
 * directly (Celery task path via `taskResponseToEnvelope`). Defensively unwrap
 * the inner `.result` when present so both call sites render correctly.
 */
function unwrapResult(data: ScriptReviewResult): ScriptReviewResult {
  const maybeWrapped = (data as unknown as { result?: unknown }).result;
  if (maybeWrapped && typeof maybeWrapped === 'object') {
    return maybeWrapped as ScriptReviewResult;
  }
  return data;
}

export function ScriptResultTabs({
  data: rawData,
  aiStatus,
  aiError,
  scriptText,
  doc,
  scriptTextLoading,
  onAddComment,
  onSelectComment,
  highlightAnchors,
  onViewComment,
  versions,
  onFetchVersionText,
  canAddLineComment = true,
  jumpToQuote,
  onJumpedToQuote,
  geoEnvelope,
  showGeo,
  onGenerateGeo,
  geoGenerating,
  onRegenerateGeo,
  onSaveGeoSelection,
  geoReadOnly,
  assignedProducts,
  campaignProductCount,
}: ScriptResultTabsProps) {
  const [urlStab, setStab] = useCampaignStabParam('overview');
  const validStab = (v: string): v is TabKey =>
    ['overview', 'script', 'lineby', 'variants', 'safety', 'geo'].includes(v);
  const [activeTab, setActiveTabState] = useState<TabKey>(
    validStab(urlStab) ? urlStab : 'overview',
  );

  const setActiveTab = useCallback((id: TabKey) => {
    setActiveTabState(id);
    setStab(id);
  }, [setStab]);

  // GEO auto-generates when the REVIEW opens (alongside the rest of the analysis),
  // NOT on GEO-tab click. This component is mounted for the whole panel, so the
  // pack is already generating by the time the brand reaches the GEO tab — no
  // manual "Generate" button. The per-entry key= on the panel remounts this and
  // resets the ref, so it fires once per opened review. Read-only (Confirmed)
  // never triggers; non-YouTube (showGeo=false) never triggers.
  const geoAutoFiredRef = useRef(false);
  const geoNoPack = !geoEnvelope || (!geoEnvelope.status && !geoEnvelope.data?.result);
  useEffect(() => {
    if (!showGeo || geoReadOnly) return;
    if (!geoAutoFiredRef.current && onGenerateGeo && !geoGenerating && geoNoPack) {
      geoAutoFiredRef.current = true;
      void onGenerateGeo();
    }
  }, [showGeo, geoReadOnly, onGenerateGeo, geoGenerating, geoNoPack]);

  const data = rawData ? unwrapResult(rawData) : undefined;
  const aiReady = aiStatus === 'complete' && !!data;

  // Script version archive: lifted here (not owned by ScriptTab) so the
  // dropdown can render in the tab-bar row alongside LinkedProductsBar
  // instead of alone above the script body. Archived-revision viewing
  // state — null viewedVersionId = the live version.
  const [viewedVersionId, setViewedVersionId] = useState<string | null>(null);
  const [archivedText, setArchivedText] = useState<string>('');
  const viewingArchived = viewedVersionId !== null;
  // Latest-wins guard for the archived-text fetch: a slow earlier fetch
  // must not clobber a newer selection (including back-to-current).
  const versionFetchSeq = useRef(0);
  const handleVersionChange = (id: string, current: boolean) => {
    const seq = ++versionFetchSeq.current;
    if (current || !onFetchVersionText) {
      setViewedVersionId(null);
      setArchivedText('');
      return;
    }
    onFetchVersionText(id)
      .then((text) => {
        if (versionFetchSeq.current !== seq) return; // superseded
        setViewedVersionId(id);
        setArchivedText(text);
      })
      .catch(() => {
        /* fetch failed — stay on whatever is currently shown */
      });
  };

  // Same gate LinkedProductsBar applies internally — mirrored here (not
  // exposed by that component) so the wrapping row can decide whether to
  // render at all, rather than leaving an empty padded row when neither
  // the product list nor the version dropdown has anything to show.
  const showProducts =
    !!assignedProducts?.length && !(campaignProductCount !== undefined && campaignProductCount < 2);
  // Disabled for now (product decision pending on placement/behavior around
  // it) — flip this back on once that's settled. When it comes back, it's
  // Script-tab-only (activeTab === 'script'), not shown across every tab
  // the way LinkedProductsBar is.
  const VERSION_DROPDOWN_ENABLED = false;
  const availableVersions = versions ?? [];
  const showVersionDropdown =
    VERSION_DROPDOWN_ENABLED && activeTab === 'script' && availableVersions.length > 1;

  // One-shot scroll target for the Script tab: set when an AI row's
  // "Comment" is clicked so the user lands ON the flagged line with the
  // chip staged — the same inline experience as selecting the text.
  const [scrollToQuote, setScrollToQuote] = useState<ScriptQuoteTarget | null>(null);
  const lineCommentHandler = useMemo(
    () =>
      canAddLineComment && onAddComment
        ? (payload: AddToCommentPayload) => {
            // Stays on Line-by-Line — no tab switch, no scroll/flash on the
            // Script tab. The row already shows the flagged line in place;
            // jumping away mid-annotation just to land back on the same
            // text was disorienting, not helpful. (Contrast with a chat
            // anchor click / select-to-comment on the Script tab itself,
            // which DO still land on Script — there's no "in place" to stay
            // at for those.)
            void onAddComment(payload);
          }
        : undefined,
    [canAddLineComment, onAddComment],
  );
  // External jump target (e.g. clicking an anchored quote in the chat
  // thread) feeds the same one-shot scroll state the AI-row "Comment"
  // click uses internally — same destination, different trigger.
  //
  // NOT the render-phase "adjust state during render" pattern (what
  // FieldCommentDot uses for its own, purely-local active-prop edge): that
  // pattern is only safe when every setState it calls is local component
  // state. setActiveTab isn't — it also writes the URL search param via
  // the router (setStab), and calling that mid-render collided with the
  // router's own Transitioner component also rendering ("Cannot update a
  // component while rendering a different component"), which silently
  // dropped the tab switch — a chat anchor's "jump to script" click
  // visibly did nothing. A real effect avoids that; the guard on
  // consumedJumpNonce keeps it from re-firing for a target already handled.
  const [consumedJumpNonce, setConsumedJumpNonce] = useState<number | null>(null);
  // Stable identity: this is a dependency of the INNER ScriptTab's own
  // scroll-and-flash effect (via its onScrolledToQuote prop). An inline
  // arrow here would give that effect a new function every render of
  // THIS component — not just when scrollToQuote itself changes — tearing
  // down and reapplying the highlight flash on every unrelated re-render.
  const handleScrolledToQuote = useCallback(() => setScrollToQuote(null), []);
  const jumpNonce = jumpToQuote?.nonce;
  useEffect(() => {
    if (!jumpToQuote || jumpNonce === undefined || jumpNonce === consumedJumpNonce) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- consumes each externally requested chat-anchor jump exactly once before switching the URL-backed tab and starting its scroll effect.
    setConsumedJumpNonce(jumpNonce);
    setActiveTab('script');
    // occurrence carried through, not just text — see MessageAnchor.occurrence
    // and jumpToQuote's own doc comment. A fresh object each time (same
    // reasoning as lineCommentHandler above) is what makes a repeat click on
    // the SAME anchor still re-trigger the scroll+flash below.
    setScrollToQuote({ text: jumpToQuote.text, occurrence: jumpToQuote.occurrence });
    onJumpedToQuote?.();
  }, [jumpNonce, consumedJumpNonce, jumpToQuote, onJumpedToQuote, setActiveTab]);
  // GEO Optimized sits just before "Safety & Compliance" (YouTube only).
  const tabs = showGeo
    ? TABS.flatMap((t) => (t.id === 'safety' ? [GEO_TAB, t] : [t]))
    : TABS;

  // Roving tabindex (WAI-ARIA APG Tabs pattern): only the active tab is in
  // the Tab order (tabIndex 0), so ArrowLeft/ArrowRight/Home/End must move
  // BOTH the active-tab state and actual DOM focus — changing tabIndex alone
  // does not move focus. tabRefs lets the keydown handler imperatively focus
  // the newly-active tab button after switching.
  const tabRefs = useRef<Partial<Record<TabKey, HTMLButtonElement | null>>>({});

  const focusTab = (id: TabKey) => {
    setActiveTab(id);
    tabRefs.current[id]?.focus();
  };

  const handleTablistKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = tabs.findIndex((t) => t.id === activeTab);
    if (currentIndex === -1) return;
    let nextIndex: number;
    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextTab = tabs[nextIndex];
    if (nextTab) focusTab(nextTab.id);
  };

  const translation = useScriptTranslation({ scriptText, data });

  return (
    <ScriptTranslationProvider value={translation}>
      <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-col">
        {/* Tabs + Link Products form one combined block (Figma 2887:60260/60252)
            — flush against each other, no gap.
            shrink-0, not sticky: ScriptReviewPanel's scroll region now wraps
            ONLY the content below (see that file's comment) — the vertical
            scrollbar runs alongside the script text, not the tab bar above
            it, and the tab bar needs no special positioning to stay put. */}
        <div className="flex w-full min-w-0 shrink-0 flex-col bg-card">
          {/* Figma "Tabs — Underline" (3072:75633): each tab is a column (label +
              a real 2px underline bar below it, not an absolute overlay), active =
              foreground-strong (#171717) text + underline, inactive = muted, gap-8
              between tabs. */}
          <div
            role="tablist"
            aria-label="Script review sections"
            onKeyDown={handleTablistKeyDown}
            className="flex min-w-0 flex-nowrap items-center gap-8 border-b border-border px-6 pt-1"
          >
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                type="button"
                variant="ghost"
                role="tab"
                id={`script-review-tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`script-review-tabpanel-${tab.id}`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                className="flex h-auto min-w-0 shrink-0 flex-col items-center justify-end gap-1 rounded-none px-0.5 pb-px hover:bg-transparent"
              >
                <span
                  className={
                    activeTab === tab.id
                      ? 'inline-flex items-center gap-1 text-body-sm-medium whitespace-nowrap text-foreground'
                      : 'inline-flex items-center gap-1 text-body-sm-medium whitespace-nowrap text-muted-foreground'
                  }
                >
                  {tab.label}
                  {tab.id === 'geo' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#6C09F7] px-1.5 py-0.5 text-white">
                      <DsSparkle size={12} weight="regular" />
                      <span className="text-caption-regular">New</span>
                    </span>
                  )}
                </span>
                <span
                  className={
                    activeTab === tab.id
                      ? 'h-0.5 w-full rounded-[2px] bg-foreground'
                      : 'h-0.5 w-full rounded-[2px] bg-transparent'
                  }
                />
              </Button>
            ))}
          </div>

          {/* Figma 2887:60252 — sits directly below the tab bar, above the tab
              content. Product list (left) and the script version archive
              (right, ml-auto) share this row instead of the version picker
              floating alone above the script body — it stays visible
              across every tab, not just Script, matching the product list's
              own always-on placement. Renders nothing when neither has
              anything to show. */}
          {showProducts || showVersionDropdown ? (
            <div className="flex w-full items-start gap-3 px-4 py-3">
              <LinkedProductsBar
                products={assignedProducts}
                campaignProductCount={campaignProductCount}
                className="w-auto flex-1 min-w-0 px-0 py-0"
              />
              {showVersionDropdown ? (
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <select
                    value={viewedVersionId ?? (availableVersions.find((v) => v.current)?.id ?? '')}
                    onChange={(e) => {
                      const v = availableVersions.find((x) => x.id === e.target.value);
                      if (v) handleVersionChange(v.id, !!v.current);
                    }}
                    aria-label="Script version"
                    className="h-8 rounded-md border border-border bg-card px-2 text-[12px] font-medium text-foreground"
                  >
                    {availableVersions.map((v) => (
                      <option key={v.id} value={v.id}>
                        v{v.version}
                        {v.current ? ' (current)' : ''}
                      </option>
                    ))}
                  </select>
                  {viewingArchived ? (
                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                      Archived revision — read-only
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Only this region scrolls — pb-5 lives here (not on the panel's
            outer wrapper) so it scrolls WITH the content, giving real
            breathing room at the true bottom instead of the bottom of a
            now-full-height, non-scrolling flex column. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-3 px-6 pt-3 pb-5">
          {translation.canTranslate ? (
            <ScriptTranslateToggle
              showingTranslation={translation.showingTranslation}
              detectedLanguage={translation.detectedLanguage}
              onToggle={translation.toggle}
            />
          ) : null}

          <div
            role="tabpanel"
            id={`script-review-tabpanel-${activeTab}`}
            aria-labelledby={`script-review-tab-${activeTab}`}
            tabIndex={0}
          >
            {activeTab === 'script' && (
              <ScriptTab
                scriptText={scriptText}
                doc={doc}
                scriptTextLoading={scriptTextLoading}
                onSelectComment={canAddLineComment ? onSelectComment : undefined}
                highlightAnchors={highlightAnchors}
                onViewComment={onViewComment}
                viewingArchived={viewingArchived}
                archivedText={archivedText}
                scrollToQuote={scrollToQuote}
                onScrolledToQuote={handleScrolledToQuote}
              />
            )}
            {/* GEO has its own envelope + status — render outside the script-review aiReady gate. */}
            {activeTab === 'geo' && (
              <GeoTab envelope={geoEnvelope} onGenerate={onGenerateGeo} generating={geoGenerating} onRegenerate={onRegenerateGeo} onSaveSelection={onSaveGeoSelection} readOnly={geoReadOnly} />
            )}
            {activeTab !== 'script' &&
              (aiReady && data ? (
                <>
                  {activeTab === 'overview' && <OverviewTab data={data} />}
                  {activeTab === 'lineby' && <LineByLineTab data={data} onAddComment={lineCommentHandler} />}
                  {activeTab === 'variants' && <VariantsTab data={data} />}
                  {activeTab === 'safety' && <SafetyTab data={data} />}
                </>
              ) : (
                <AiNotReady status={aiStatus} error={aiError} />
              ))}
          </div>
          </div>
        </div>
      </div>
    </ScriptTranslationProvider>
  );
}

/** Empty / pending / failed state for AI-driven tabs. Script tab
 *  remains readable while this renders. */
function AiNotReady({ status, error }: { status?: string; error?: string }) {
  if (status === 'pending' || status === 'processing') {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-body-sm-regular text-muted-foreground">
        <Sparkle size={16} className="animate-pulse text-violet-500" />
        AI script review is running… check back in a minute.
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-body-sm-regular text-rose-600">
        <XCircle size={20} />
        AI script review failed{error ? `: ${error}` : '.'}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center py-16 text-body-sm-regular text-muted-foreground">
      No AI analysis yet for this script.
    </div>
  );
}

// ─── Script tab ─────────────────────────────────────────────────────────────

/** Count non-overlapping occurrences of `needle` in `haystack`. */
function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let idx = haystack.indexOf(needle);
  while (idx !== -1) {
    count += 1;
    idx = haystack.indexOf(needle, idx + needle.length);
  }
  return count;
}

/**
 * The script body lives inside an `overflow-y-auto` region (its own scroll
 * container, one level up from ScriptTab — see ScriptResultTabs' own JSX).
 * Both floating pills sit ~40-44px ABOVE their anchor line; for a line near
 * the top of the CURRENT scroll position, that top could land above this
 * ancestor's visible edge and get clipped by its overflow — rendered, but
 * invisible. Walks up from `el` to find the nearest such ancestor so the
 * pill's vertical position can be clamped against its real visible bounds,
 * not just its own wrapper's.
 */
function nearestScrollAncestor(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return el;
}

/** Read-only render of the script body. The original renders from its native
 *  Lexical document (formatting preserved 1:1 with the editor) inside the same
 *  highlighted prose container the comment system walks; a translation (or a
 *  legacy script with no doc) is plain text, rendered as GFM markdown with the
 *  Tailwind prose styling so headings / lists / code blocks look reasonable
 *  without a bespoke stylesheet.
 *
 *  With `onSelectComment` wired, selecting any span surfaces a floating
 *  "Comment" button (single action — a first comment and a reply are the
 *  same thing, PRD §4.2). Selection + occurrence both live in the
 *  RENDERED text domain (container.textContent), never markdown source
 *  offsets — they don't survive rendering. */
function ScriptTab({
  scriptText,
  doc,
  scriptTextLoading,
  onSelectComment,
  highlightAnchors,
  onViewComment,
  viewingArchived,
  archivedText,
  scrollToQuote,
  onScrolledToQuote,
}: {
  /** No production caller populates this anymore — see the doc comment on
   *  the outer component's `scriptText` prop. */
  scriptText?: string;
  /** The script's native Lexical document. Always present in practice;
   *  renders the original when not viewing a translation/archived version. */
  doc?: ScriptDoc;
  scriptTextLoading?: boolean;
  onSelectComment?: (payload: SelectCommentPayload) => void;
  highlightAnchors?: HighlightableAnchor[];
  onViewComment?: (messageId: string) => void;
  /** Whether an archived (non-current) revision is being viewed — the
   *  version dropdown itself lives one level up, in ScriptResultTabs'
   *  tab-bar row, alongside LinkedProductsBar. */
  viewingArchived: boolean;
  /** The archived revision's markdown, when viewingArchived is true. */
  archivedText: string;
  /** One-shot: scroll to this quote (at its own occurrence) and flash it —
   *  an AI row's Comment click or a chat-anchor jump lands the user here. */
  scrollToQuote?: ScriptQuoteTarget | null;
  onScrolledToQuote?: () => void;
}) {
  const { showingTranslation, bodyText } = useScriptTranslationContext();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // The text domain for selection capture, occurrence counting, and the
  // highlight walker is the PROSE container only — never the wrapper,
  // whose chrome (style tag, floating button) would pollute offsets and
  // shift occurrences as it appears/disappears.
  const proseRef = useRef<HTMLDivElement | null>(null);
  const [pending, setPending] = useState<
    (SelectCommentPayload & { top: number; left: number }) | null
  >(null);
  // Both pills are horizontally centered on their `left` via translateX
  // (-50%) — the layout effects below measure each button's own rendered
  // width once painted and nudge `left` back inside the wrapper's bounds
  // if centering it on the cursor would otherwise hang half the pill off
  // the left or right edge (its text getting visually cut off/truncated
  // by the wrapper's overflow).
  const pendingBtnRef = useRef<HTMLButtonElement | null>(null);
  const hoveredBtnRef = useRef<HTMLButtonElement | null>(null);
  // The (anchor, range) pairs applyAnchorHighlights actually resolved this
  // pass — hover-hit-testing (below) checks the pointer against these same
  // ranges rather than re-walking the DOM on every mousemove. A ref, not
  // state: it's read inside a raw event listener, not rendered directly.
  const resolvedAnchorsRef = useRef<ResolvedAnchorHighlight[]>([]);
  // The read-only Lexical render fills proseRef asynchronously after mount, so
  // the highlight effect can't rely on `bodyText` alone to know the DOM is
  // ready. ScriptDocRenderer calls onReady each time its editor state settles
  // (once per doc); the tick advances then, re-running the highlight effect
  // against the now-populated container — including on a later doc/version swap.
  const [docReadyTick, setDocReadyTick] = useState(0);
  const handleDocReady = useCallback(() => setDocReadyTick((t) => t + 1), []);
  // Persistent highlights: paint every open-comment anchor over the
  // rendered script (PRD §4.2 — see where comments are without opening
  // chat). CSS Custom Highlight API — no DOM mutation, degrades to
  // nothing on unsupported browsers; unresolvable quotes are skipped.
  useEffect(() => {
    const container = proseRef.current;
    // Highlights only apply to the live version — archived text has its
    // own anchors pinned to that revision, out of scope for v1 viewing.
    if (!container || !highlightAnchors?.length || viewingArchived) {
      resolvedAnchorsRef.current = [];
      clearAnchorHighlights();
      return;
    }
    applyAnchorHighlights(container, highlightAnchors, (resolved) => {
      resolvedAnchorsRef.current = resolved;
    });
    return () => {
      resolvedAnchorsRef.current = [];
      clearAnchorHighlights();
    };
  }, [highlightAnchors, bodyText, viewingArchived, docReadyTick]);

  // "View comment" hover affordance: hovering a highlighted (already-
  // commented) span surfaces a pill mirroring the Add Comment one; clicking
  // it jumps to that comment in chat (the reverse of onJumpToAnchor).
  //
  // The CSS Custom Highlight API paints ranges with no real DOM element —
  // there's nothing to attach a native mouseenter to — so hover is detected
  // geometrically: on mousemove, test the pointer against each resolved
  // anchor's own Range.getClientRects() (the same Ranges just painted
  // above). A time-based grace period (not a position-based "hoverable
  // island") keeps the pill up while the pointer crosses from the
  // highlighted text onto the button itself — cancelled by re-entering
  // either the highlight or the button, same pattern any hover-triggered
  // tooltip/menu uses for content it doesn't structurally contain.
  const [hoveredComment, setHoveredComment] = useState<
    { messageId: string; top: number; left: number } | null
  >(null);
  // Mirrors of `pending`/`hoveredComment` for the mousemove handler below to
  // read without being one of the effect's own dependencies — pending and
  // hoveredComment themselves change on every hover-in/hover-out/click, and
  // having the listener effect depend on them meant tearing down and
  // re-attaching the native mousemove listener on every single one of those
  // transitions (confirmed empirically: dozens of add/removeEventListener
  // cycles from a handful of hovers). Attaching once and reading the latest
  // value through a ref is the standard fix for a value an event handler
  // needs live without forcing the subscription itself to churn.
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  const hoveredCommentRef = useRef(hoveredComment);
  useEffect(() => {
    hoveredCommentRef.current = hoveredComment;
  }, [hoveredComment]);
  const hideHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelHideHover = useCallback(() => {
    if (hideHoverTimeoutRef.current !== null) {
      clearTimeout(hideHoverTimeoutRef.current);
      hideHoverTimeoutRef.current = null;
    }
  }, []);
  const scheduleHideHover = useCallback(() => {
    if (hideHoverTimeoutRef.current !== null) return;
    hideHoverTimeoutRef.current = setTimeout(() => {
      hideHoverTimeoutRef.current = null;
      setHoveredComment(null);
    }, 200);
  }, []);
  useEffect(() => cancelHideHover, [cancelHideHover]);

  // Post-render edge clamp for both pills — see pendingBtnRef's own doc
  // comment. Runs in a layout effect (not a regular one) so the correction
  // lands before the browser paints, avoiding a visible jump. Self-
  // terminating: once `left` already accounts for the button's own
  // measured width, the next pass computes the same value and skips the
  // update.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const btn = pendingBtnRef.current;
    if (!pending || !wrapper || !btn) return;
    const wrapperWidth = wrapper.getBoundingClientRect().width;
    // jsdom (unit tests) never lays anything out — every rect comes back
    // zeroed. Clamping against a 0px wrapper would fight the fixture
    // widths those tests set up, so treat "no real layout" as a no-op
    // rather than a legitimately zero-width container.
    if (wrapperWidth === 0) return;
    const halfWidth = btn.offsetWidth / 2;
    const margin = 4;
    const clampedLeft = Math.min(
      Math.max(pending.left, halfWidth + margin),
      wrapperWidth - halfWidth - margin,
    );
    if (Math.abs(clampedLeft - pending.left) > 0.5) {
      setPending((p) => (p ? { ...p, left: clampedLeft } : p));
    }
  }, [pending]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const btn = hoveredBtnRef.current;
    if (!hoveredComment || !wrapper || !btn) return;
    const wrapperWidth = wrapper.getBoundingClientRect().width;
    if (wrapperWidth === 0) return;
    const halfWidth = btn.offsetWidth / 2;
    const margin = 4;
    const clampedLeft = Math.min(
      Math.max(hoveredComment.left, halfWidth + margin),
      wrapperWidth - halfWidth - margin,
    );
    if (Math.abs(clampedLeft - hoveredComment.left) > 0.5) {
      setHoveredComment((c) => (c ? { ...c, left: clampedLeft } : c));
    }
  }, [hoveredComment]);

  useEffect(() => {
    if (!onViewComment || viewingArchived) return;
    const container = proseRef.current;
    const wrapper = wrapperRef.current;
    if (!container || !wrapper) return;
    const scrollAncestor = nearestScrollAncestor(wrapper);

    const onMouseMove = (ev: MouseEvent) => {
      // Never compete with an active Add Comment button — the two floating
      // pills would otherwise stack when hovering already-commented text
      // mid-selection.
      if (pendingRef.current) return;
      const { clientX, clientY } = ev;
      for (const { anchor, range } of resolvedAnchorsRef.current) {
        if (!anchor.messageId) continue;
        // jsdom's Range doesn't implement getClientRects (same gap as
        // getBoundingClientRect elsewhere in this file) — degrade to "no
        // hit" rather than throw on every mousemove in that environment.
        if (typeof range.getClientRects !== 'function') continue;
        const rects = range.getClientRects();
        let hitRect: DOMRect | null = null;
        for (let i = 0; i < rects.length; i += 1) {
          const r = rects[i];
          if (!r) continue;
          if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
            hitRect = r;
            break;
          }
        }
        if (!hitRect) continue;
        cancelHideHover();
        // Once shown for a given anchor, the pill stays PUT — it doesn't
        // chase the cursor as it wanders around inside the same highlighted
        // span. Only entering a DIFFERENT anchor (or re-entering after
        // leaving) computes a fresh position. Without this, the pill
        // jittered constantly on every mousemove within a multi-word/
        // multi-line highlight.
        if (hoveredCommentRef.current?.messageId === anchor.messageId) return;
        // Vertically pinned to the LINE the cursor is over (that line's own
        // rect top) — industry-standard editor popups (Google Docs, Notion)
        // don't track the raw pointer Y, which jitters as the cursor moves
        // within a line's height. Horizontally it DOES follow the cursor —
        // a highlighted span can be far wider than the cursor, so pinning
        // horizontally to e.g. the line's own start would put the button
        // away from wherever the user is actually hovering.
        const wrapperRect = wrapper.getBoundingClientRect();
        // Clamped against the scroll container's own visible top, not just
        // 0 — for a line near the top of the CURRENT scroll position, the
        // unclamped "40px above the line" position lands above the
        // scrollable ancestor's edge and gets silently clipped by its own
        // overflow: rendered, but invisible. This is why the button can
        // fail to appear even though the highlight itself is visible.
        const minTop = scrollAncestor.getBoundingClientRect().top - wrapperRect.top + 4;
        setHoveredComment({
          messageId: anchor.messageId,
          top: Math.max(minTop, hitRect.top - wrapperRect.top - 40),
          left: clientX - wrapperRect.left,
        });
        return;
      }
      scheduleHideHover();
    };
    container.addEventListener('mousemove', onMouseMove);
    return () => container.removeEventListener('mousemove', onMouseMove);
    // pending/hoveredComment deliberately excluded — read live via the refs
    // above instead, so this listener attaches once instead of on every
    // hover transition. See pendingRef's own doc comment for why.
  }, [onViewComment, viewingArchived, cancelHideHover, scheduleHideHover]);

  // Land on the flagged line when arriving from an AI row's Comment click
  // (or a chat anchor click) — scrolls there and hands the quote to
  // useFlashHighlight. scrollToQuote is deliberately one-shot:
  // onScrolledToQuote nulls it right after this run, so clicking the SAME
  // anchor again still retriggers a jump. That immediate reset would tear
  // down a highlight+timer applied IN THIS SAME effect (its own cleanup
  // running practically the same tick it was set up) — a live version of
  // this genuinely did apply the highlight (confirmed: registry size went
  // 1 -> 2) only to have it cleared before the next paint. useFlashHighlight
  // owns separate, un-clearing state so the flash's lifecycle isn't coupled
  // to scrollToQuote's self-reset.
  const flash = useFlashHighlight(proseRef, bodyText);
  useEffect(() => {
    if (!scrollToQuote || viewingArchived) return;
    const container = proseRef.current;
    if (!container) return;
    // NOT .trim()'d — an anchor's quoted_text can carry a real leading/
    // trailing space as part of exactly what the user selected (a 515-char
    // anchor in the wild does), and the persistent open-comment highlights
    // (applyAnchorHighlights) never trim either. Trimming here shifted the
    // flash a character off the purple highlight for that same quote —
    // same text, visibly misaligned highlights.
    // occurrence, not a hardcoded 0 — a script with the same phrase twice
    // (a repeated tagline/CTA) must jump to the SAME span its persistent
    // purple highlight is already sitting on, not always the first match.
    const range = findAnchorRange(container, scrollToQuote.text, scrollToQuote.occurrence);
    onScrolledToQuote?.();
    if (!range) {
      // The quote isn't in the CURRENT script text — e.g. an anchor from a
      // chat message whose script_version_id is missing/stale, so the
      // version guard upstream let it through. Failing silently here would
      // look identical to a working jump that just didn't need to scroll
      // (short script, target already on screen) — say something instead.
      toast.error("This comment's text is no longer in the script.");
      return;
    }
    const target = range.startContainer.parentElement;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Move focus too, not just the scroll position — same reasoning as
    // ChatThread's jumpToMessage. `target` is an arbitrary element inside
    // rendered markdown (whatever ReactMarkdown produced for this
    // paragraph/list-item/etc.), not one we author with a tabIndex prop —
    // set it imperatively so it becomes a valid, if not normally
    // tab-reachable, focus target. Left in place afterward (not reset to
    // its prior tabIndex): harmless on a non-interactive element, and the
    // standard technique for "scroll-to-and-focus" targets that aren't
    // naturally focusable (MDN's own recommendation for this exact case).
    if (target && !target.hasAttribute('tabindex')) target.tabIndex = -1;
    target?.focus({ preventScroll: true });
    flash(scrollToQuote);
  }, [scrollToQuote, viewingArchived, bodyText, onScrolledToQuote, flash]);

  // The mouse position at the moment the selection gesture ended (mouseup)
  // — captured by the gesture-end effect below, read by handleSelectionEnd
  // so the "Comment" button lands next to where the user's cursor actually
  // is, not centered over the (possibly multi-line) selection. Null for a
  // keyboard-driven selection (Shift+Arrow, no mouse involved), which has
  // no meaningful cursor position to anchor to.
  const lastSelectionPointerRef = useRef<{ x: number; y: number } | null>(null);

  // Selection → floating "Comment" button. Reads live off window.getSelection()
  // rather than an event's own payload, so it doesn't matter what caused the
  // selection to change (mouse drag, keyboard/caret browsing, screen-reader
  // virtual cursor, programmatic).
  const handleSelectionEnd = useCallback(() => {
    if (!onSelectComment) return;
    const container = proseRef.current;
    const wrapper = wrapperRef.current;
    const sel = window.getSelection();
    if (!container || !wrapper || !sel || sel.isCollapsed || sel.rangeCount === 0) {
      setPending(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
      setPending(null);
      return;
    }
    // Range.toString() — NOT Selection.toString() — so the quote lives in
    // the same text domain as the highlight walker (raw text-node data,
    // no rendered newlines at block boundaries). A multi-paragraph quote
    // captured via Selection.toString() would never re-match.
    let quoted = range.toString();
    if (!quoted.trim()) {
      setPending(null);
      return;
    }
    // BE caps anchors at 2000 runes — cap here so a huge selection sends
    // (highlighting the first 2000 chars) instead of failing validation.
    // Back off one unit if the cut would split a surrogate pair.
    if (quoted.length > 2000) {
      const cut = /[\uD800-\uDBFF]/.test(quoted.charAt(1999)) ? 1999 : 2000;
      quoted = quoted.slice(0, cut);
    }
    // Occurrence = how many identical spans appear BEFORE the selection,
    // measured against the same prose text domain.
    const pre = document.createRange();
    pre.selectNodeContents(container);
    pre.setEnd(range.startContainer, range.startOffset);
    const occurrence = countOccurrences(pre.toString(), quoted);

    const wrapperRect = wrapper.getBoundingClientRect();
    // jsdom's Range has neither getBoundingClientRect nor getClientRects —
    // degrade to a single zeroed rect there.
    const boundingRect: Pick<DOMRect, 'top' | 'left' | 'width'> =
      typeof range.getBoundingClientRect === 'function'
        ? range.getBoundingClientRect()
        : { top: wrapperRect.top, left: wrapperRect.left, width: 0 };
    const lineRects: DOMRect[] =
      typeof range.getClientRects === 'function' ? Array.from(range.getClientRects()) : [];

    // Pill button (Figma 3405:75006), centered horizontally via
    // translateX(-50%) below. Vertically pinned to the LINE the cursor
    // released on (that line's own rect top), not the raw mouseup Y —
    // industry-standard editor popups (Google Docs, Notion) don't track the
    // pointer vertically, since it jitters as the hand moves within a
    // line's height. Horizontally it DOES follow the cursor: a drag can
    // span multiple lines, and centering over the whole selection (the old
    // behavior) could land the button far from wherever the user's hand
    // actually is. Both fall back to the selection's own bounding rect for
    // a keyboard-driven selection, which has no cursor position to anchor to.
    const pointer = lastSelectionPointerRef.current;
    let lineTop = boundingRect.top;
    if (pointer && lineRects.length > 0) {
      const onLine = lineRects.find((r) => pointer.y >= r.top && pointer.y <= r.bottom);
      const closest =
        onLine ??
        lineRects.reduce((a, b) =>
          Math.abs(a.top + a.height / 2 - pointer.y) <= Math.abs(b.top + b.height / 2 - pointer.y)
            ? a
            : b,
        );
      lineTop = closest.top;
    }
    // Clamped against the scroll container's own visible top (not just 0)
    // — see nearestScrollAncestor's doc comment: an unclamped "44px above
    // the line" can land above the scrollable ancestor's edge for a
    // selection near the top of the current scroll position and get
    // silently clipped by its own overflow.
    const minTop = nearestScrollAncestor(wrapper).getBoundingClientRect().top - wrapperRect.top + 4;
    setPending({
      quotedText: quoted,
      occurrence,
      top: Math.max(minTop, lineTop - wrapperRect.top - 44),
      left: Math.max(
        0,
        (pointer ? pointer.x : boundingRect.left + boundingRect.width / 2) - wrapperRect.left,
      ),
    });
  }, [onSelectComment]);

  // Google Docs' own trigger: react to the gesture finishing (mouseup for a
  // click/drag/double/triple-click, keyup for keyboard selection), not to
  // the browser's raw `selectionchange` event. selectionchange fires on
  // every pixel of a drag (would chase the cursor) and can also fire for
  // reasons that have nothing to do with the user actively selecting;
  // mouseup/keyup each fire exactly once per discrete action, so there's no
  // drag-chasing and no stale-selection replay to guard against — a plain
  // scroll never fires either of them.
  //
  // The read is deferred one animation frame rather than done synchronously
  // in the event handler: in some browsers window.getSelection() hasn't
  // finished updating to match the just-completed gesture by the time
  // mouseup/keyup's own listeners run, so an immediate read reacts to the
  // PREVIOUS selection and only catches up on the next interaction (the
  // "double tap" bug). One frame is enough for the browser to catch up.
  //
  // Document-wide (not scoped to the script container) so a click OUTSIDE
  // the script — which collapses the selection — also dismisses the button,
  // not just a click back inside it.
  useEffect(() => {
    if (!onSelectComment || viewingArchived) return;
    const onGestureEnd = (ev: Event) => {
      lastSelectionPointerRef.current =
        ev instanceof MouseEvent ? { x: ev.clientX, y: ev.clientY } : null;
      requestAnimationFrame(handleSelectionEnd);
    };
    document.addEventListener('mouseup', onGestureEnd);
    document.addEventListener('keyup', onGestureEnd);
    return () => {
      document.removeEventListener('mouseup', onGestureEnd);
      document.removeEventListener('keyup', onGestureEnd);
    };
  }, [onSelectComment, viewingArchived, handleSelectionEnd]);

  // A doc-backed script has content even when the legacy plain-text
  // `scriptText` is empty, so the empty-state gates on both.
  const hasScript = plainTextFromDoc(doc).trim().length > 0 || !!(scriptText && scriptText.trim());
  if (!hasScript) {
    // Script review implies a script exists — an empty scriptText while
    // its own fetch is still in flight is just the sidebar opening, not a
    // real "no script" state. Rendering the empty state during that window
    // meant it flashed on every open, right before the real text swapped
    // in. A skeleton reads as "loading" instead of "there's nothing here".
    if (scriptTextLoading) {
      return (
        <div className="flex flex-col gap-3 px-1 py-2" aria-hidden="true">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <FileText size={28} className="text-muted-foreground" />
        <p className="text-body-sm-semibold text-muted-foreground">No script text yet.</p>
        <p className="text-caption-regular text-muted-foreground">
          The admin hasn&apos;t uploaded a script for this version.
        </p>
      </div>
    );
  }

  // A translation has no doc and its text is a different domain than the
  // original's comment anchors — render it as plain GFM markdown with no
  // comment chrome (no proseRef ⇒ selection capture and highlights are
  // inert), mirroring the archived-view gating. The original and legacy
  // (no-doc) views fall through to the highlighted container below.
  if (showingTranslation) {
    return (
      <div className="px-1 py-2 text-body-sm-regular leading-relaxed text-foreground">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyText}</ReactMarkdown>
      </div>
    );
  }

  // The original renders from its native Lexical doc; a legacy (no-doc) or
  // archived revision renders plain markdown. All three fill the same
  // highlighted proseRef container so the comment system walks their text
  // nodes identically; highlights themselves are gated off for archived.
  // `doc` is the last operand so a truthy result narrows to ScriptDoc (drops the
  // non-null assertion at the render site below).
  const showDoc = !viewingArchived && !showingTranslation && doc;

  return (
    <div ref={wrapperRef} className="relative">
      {/* ::highlight() can't be expressed in Tailwind — tiny scoped rules. */}
      <style>{`${OPEN_HIGHLIGHT_CSS}
::highlight(${SCRIPT_HIGHLIGHT_FOCUS_NAME}) { background-color: rgba(250, 204, 21, var(--script-jump-flash-alpha, 0.45)); }`}</style>
      {pending ? (
        <button
          ref={pendingBtnRef}
          type="button"
          aria-label="Add comment"
          onMouseDown={(e) => e.preventDefault() /* keep the selection alive through the click */}
          onClick={() => {
            onSelectComment?.({ quotedText: pending.quotedText, occurrence: pending.occurrence });
            setPending(null);
            window.getSelection()?.removeAllRanges();
          }}
          // Figma 3405:75006: a solid black pill (icon + "Comment" label),
          // not the old icon-only circle — matches "View comment" below so
          // the two floating affordances read as one family. translateX
          // centers it on `left` (the selection's own center), since the
          // pill's rendered width isn't fixed the way the old 28px circle's
          // was.
          className="absolute z-10 flex h-8 shrink-0 -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#18181B] px-3 shadow-[0_2px_8px_rgba(0,0,0,0.25)] transition-transform hover:scale-105 hover:bg-[#18181B]/90"
          style={{ top: pending.top, left: pending.left }}
        >
          <ChatCircleText size={14} weight="regular" className="text-white" />
          <span className="text-caption-medium text-white">Comment</span>
        </button>
      ) : null}
      {hoveredComment ? (
        <button
          ref={hoveredBtnRef}
          type="button"
          aria-label="View comment"
          onMouseEnter={cancelHideHover}
          onMouseLeave={scheduleHideHover}
          onClick={() => {
            onViewComment?.(hoveredComment.messageId);
            setHoveredComment(null);
          }}
          // Same pill family as "Add comment" above — same shape/size/
          // position math — but for a span that ALREADY has a comment:
          // hovering it offers a way back to that comment in chat instead
          // of starting a new one.
          className="absolute z-10 flex h-8 shrink-0 -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#18181B] px-3 shadow-[0_2px_8px_rgba(0,0,0,0.25)] transition-transform hover:scale-105 hover:bg-[#18181B]/90"
          style={{ top: hoveredComment.top, left: hoveredComment.left }}
        >
          <ChatCircleText size={14} weight="fill" className="text-white" />
          <span className="text-caption-medium text-white">View comment</span>
        </button>
      ) : null}
      <div ref={proseRef} className="prose prose-sm max-w-none px-1 py-2 text-foreground">
        {showDoc ? (
          // The rich Lexical doc fills the highlighted container; onReady fires
          // once its editor state settles so the highlight effect repaints
          // against the now-populated DOM.
          <ScriptDocRenderer doc={showDoc} onReady={handleDocReady} />
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {viewingArchived ? archivedText : bodyText}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────

function OverviewTab({ data }: { data: ScriptReviewResult }) {
  const exec = data.executive_summary;
  return (
    <div className="flex flex-col gap-6">
      {/* Manual review banner */}
      {data.human_in_loop?.requires_manual_review ? (
        <ManualReviewBanner reasons={data.human_in_loop.reasons ?? []} />
      ) : null}

      {/* Hero + scoreboard in ONE container (Figma 1672-30543 "ScriptCard"):
          the Overall gauge, verdict headline/summary and Hook/Pacing/CTA sit
          on top; the Brand-fit / Compliance / Quality donuts form a divided
          footer row. Figma uses the subtle border + radius-md (8px), not the
          default border/12px used for other cards on this tab. */}
      <section className="overflow-hidden rounded-lg border border-border-subtle bg-card">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start">
          <HeroGauge score={data.overall_score ?? 0} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {exec?.one_line_for_brand ? (
              <h3 className="m-0 text-heading-h6 font-semibold leading-snug text-foreground">
                {exec.one_line_for_brand}
              </h3>
            ) : null}
            {data.feedback ? (
              <p className="m-0 text-body-sm-regular leading-relaxed text-muted-foreground">
                {data.feedback}
              </p>
            ) : null}
            {data.hook_pacing_cta_scores ? (
              <div className="mt-2 flex flex-wrap items-start gap-x-10 gap-y-3">
              <HpcInline
                label="Hook"
                value={data.hook_pacing_cta_scores.hook_strength}
                detail={
                  data.hook_pacing_cta_detail?.hook
                    ? [
                      { label: 'Quote', value: data.hook_pacing_cta_detail.hook.quote },
                      { label: 'Pattern', value: data.hook_pacing_cta_detail.hook.pattern_type },
                      { label: 'Timing', value: `${data.hook_pacing_cta_detail.hook.timing_seconds}s` },
                      { label: 'Reasoning', value: data.hook_pacing_cta_detail.hook.reasoning },
                    ]
                    : undefined
                }
              />
              <HpcInline
                label="Pacing"
                value={data.hook_pacing_cta_scores.pacing}
                detail={
                  data.hook_pacing_cta_detail?.pacing
                    ? [
                      { label: 'WPM', value: String(data.hook_pacing_cta_detail.pacing.wpm_estimate) },
                      {
                        label: 'Slow segments',
                        value: data.hook_pacing_cta_detail.pacing.slow_segments?.join(' · ') || '—',
                      },
                      { label: 'Reasoning', value: data.hook_pacing_cta_detail.pacing.reasoning },
                    ]
                    : undefined
                }
              />
              {data.hook_pacing_cta_scores.cta_clarity != null ? (
                <HpcInline
                  label="CTA"
                  value={data.hook_pacing_cta_scores.cta_clarity}
                  detail={
                    data.hook_pacing_cta_detail?.cta
                      ? [
                        { label: 'Quote', value: data.hook_pacing_cta_detail.cta.quote },
                        {
                          label: 'Action verb',
                          value: data.hook_pacing_cta_detail.cta.action_verb_present ? 'yes' : 'no',
                        },
                        {
                          label: 'Urgency',
                          value: data.hook_pacing_cta_detail.cta.urgency_present ? 'yes' : 'no',
                        },
                        { label: 'Reasoning', value: data.hook_pacing_cta_detail.cta.reasoning },
                      ]
                      : undefined
                  }
                />
              ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* Scoreboard footer — Brand fit / Compliance / Quality donuts,
            divided cells, each click-to-expand for its pillar breakdown. */}
        <div className="grid grid-cols-1 divide-y divide-border-subtle border-t border-border-subtle sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <div className="p-4">
            <MiniDonut title="Brand fit" score={data.brand_fit_score ?? 0} breakdown={data.brand_fit_breakdown} />
          </div>
          <div className="p-4">
            <MiniDonut title="Compliance" score={data.compliance_score ?? 0} breakdown={data.compliance_breakdown} />
          </div>
          <div className="p-4">
            <MiniDonut title="Quality" score={data.quality_score ?? 0} breakdown={data.quality_breakdown} />
          </div>
        </div>
      </section>

      {/* Executive Summary — the verdict headline + summary now live in the
          hero above, so this section carries the confidence chips, wins and
          risks. Omitted entirely when there's no enriched exec payload. */}
      {exec ? (
        <section className="rounded-lg border border-border bg-card p-5">
          <h4 className="m-0 mb-3 text-body-sm-medium text-muted-foreground">Executive summary</h4>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 font-semibold text-violet-700">
                Confidence: {exec.verdict_confidence}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 font-semibold text-foreground">
                Approval likelihood:{' '}
                {Math.round((exec.estimated_approval_probability ?? 0) * 100)}%
              </span>
            </div>
            {exec.top_wins?.length > 0 ? (
              <div>
                <p className="m-0 mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Top wins
                </p>
                <ul className="m-0 flex flex-col gap-1 list-none p-0">
                  {exec.top_wins.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 text-body-sm-regular text-foreground">
                      <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-500" />
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {exec.top_risks?.length > 0 ? (
              <div>
                <p className="m-0 mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Top risks
                </p>
                <ul className="m-0 flex flex-col gap-1 list-none p-0">
                  {exec.top_risks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-body-sm-regular text-foreground">
                      <Warning size={14} className="mt-0.5 shrink-0 text-amber-500" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Strategist's Deep Reasoning (enriched additive) */}
      {data.reasoning ? (
        <section className="rounded-lg border border-border bg-card p-5">
          <h4
            className="m-0 mb-3 w-fit text-caption-medium"
            style={{
              background: 'linear-gradient(90deg, #6C09F7 0%, #9333EA 50%, #F97316 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Strategic deep reasoning
          </h4>
          <div className="text-body-sm-regular text-foreground leading-relaxed whitespace-pre-wrap">
            {data.reasoning}
          </div>
        </section>
      ) : null}

      {/* Strengths */}
      {data.strengths?.length > 0 ? (
        <section className="rounded-lg border border-border bg-card p-5">
          <h4 className="m-0 mb-3 text-body-sm-medium text-foreground">Strengths</h4>
          <ul className="m-0 flex flex-col gap-2 list-none p-0">
            {data.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-body-sm-regular text-foreground">
                <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-emerald-500" />
                {s}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// ─── Line-by-Line ─────────────────────────────────────────────────────────

type LineRow = ScriptReviewResult['line_by_line_feedback'][number];

function LineByLineTab({
  data,
  onAddComment,
}: {
  data: ScriptReviewResult;
  onAddComment?: (payload: AddToCommentPayload) => void | Promise<void>;
}) {
  const rows = data.line_by_line_feedback ?? [];
  const hasSeverity = rows.some((r) => !!r.severity);
  const hasBeats = rows.some((r) => !!r.beat_label);

  // Severity sort: critical > major > minor > undefined
  const sevOrder: Record<string, number> = { critical: 0, major: 1, minor: 2 };
  const sortedRows: LineRow[] = hasSeverity
    ? [...rows].sort(
      (a, b) =>
        (sevOrder[a.severity ?? 'zz'] ?? 99) - (sevOrder[b.severity ?? 'zz'] ?? 99),
    )
    : rows;

  return (
    <div className="flex flex-col gap-4">
      <h4 className="m-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Line-by-line analysis
      </h4>
      {hasBeats ? (
        <BeatGroupedTable
          rows={sortedRows}
          hasSeverity={hasSeverity}
          onAddComment={onAddComment}
        />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <FeedbackTable
            rows={sortedRows}
            hasSeverity={hasSeverity}
            onAddComment={onAddComment}
          />
        </div>
      )}
    </div>
  );
}

function BeatGroupedTable({
  rows,
  hasSeverity,
  onAddComment,
}: {
  rows: LineRow[];
  hasSeverity: boolean;
  onAddComment?: (payload: AddToCommentPayload) => void | Promise<void>;
}) {
  const groups = new Map<string, LineRow[]>();
  rows.forEach((r) => {
    const key = r.beat_label ?? 'Ungrouped';
    const bucket = groups.get(key);
    if (bucket) bucket.push(r);
    else groups.set(key, [r]);
  });
  return (
    <div className="flex flex-col gap-3">
      {Array.from(groups.entries()).map(([beat, beatRows]) => (
        <BeatGroup
          key={beat}
          beat={beat}
          rows={beatRows}
          hasSeverity={hasSeverity}
          onAddComment={onAddComment}
        />
      ))}
    </div>
  );
}

function BeatGroup({
  beat,
  rows,
  hasSeverity,
  onAddComment,
}: {
  beat: string;
  rows: LineRow[];
  hasSeverity: boolean;
  onAddComment?: (payload: AddToCommentPayload) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted hover:bg-muted rounded-none text-left h-auto"
      >
        <span className="text-[11px] font-bold uppercase tracking-wider text-foreground">{beat}</span>
        <span className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">{rows.length} note(s)</span>
          {open ? (
            <CaretUp size={14} className="text-muted-foreground" />
          ) : (
            <CaretDown size={14} className="text-muted-foreground" />
          )}
        </span>
      </Button>
      {open ? (
        <FeedbackTable rows={rows} hasSeverity={hasSeverity} onAddComment={onAddComment} />
      ) : null}
    </div>
  );
}

function FeedbackTable({
  rows,
  hasSeverity,
  onAddComment,
}: {
  rows: LineRow[];
  hasSeverity: boolean;
  onAddComment?: (payload: AddToCommentPayload) => void | Promise<void>;
}) {
  const { pick } = useScriptTranslationContext();
  return (
    <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
      <thead>
        <tr className="border-b border-border text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          {hasSeverity ? <th className="px-4 py-3 font-semibold">Severity</th> : null}
          <th className="px-4 py-3 font-semibold">Aspect</th>
          <th className="px-4 py-3 font-semibold">Original text</th>
          <th className="px-4 py-3 font-semibold">Issue identified</th>
          <th className="px-4 py-3 font-semibold">Suggested edit</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item, idx) => {
          const suggestion = pick(item.suggestion);
          return (
            <tr key={idx} className="border-t border-border text-body-sm-regular">
              {hasSeverity ? (
                <td className="p-4 align-top">
                  {item.severity ? <SeverityCell severity={item.severity} /> : null}
                </td>
              ) : null}
              <td className="p-4 align-top">
                <div className="flex flex-wrap items-center gap-2">
                  {(() => {
                    const tone = aspectTone(item.aspect || 'general');
                    return (
                      <span className={cn('inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize', tone.bg, tone.text, tone.border)}>
                        {(item.aspect || 'general').replace(/_/g, ' ')}
                      </span>
                    );
                  })()}
                  {typeof item.confidence === 'number' ? (
                    <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      {Math.round(item.confidence * 100)}%
                    </span>
                  ) : null}
                </div>
              </td>
              <td className="p-4 align-top text-muted-foreground" style={{ width: '28%' }}>
                &ldquo;{pick(item.original_text)}&rdquo;
              </td>
              <td className="p-4 align-top text-foreground" style={{ width: '28%' }}>
                <div className="flex items-start gap-2">
                  <span className="mt-[6px] size-1.5 shrink-0 rounded-full bg-red-500" />
                  <span>{item.issue}</span>
                </div>
              </td>
              <td className="p-4 align-top text-foreground" style={{ width: '28%' }}>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <span className="mt-[6px] size-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span>{suggestion}</span>
                  </div>
                  {onAddComment ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        onAddComment({
                          originalText: pick(item.original_text),
                          aspect: item.aspect,
                          issue: item.issue,
                          suggestion,
                        })
                      }
                      className="ml-3.5 h-auto self-start rounded-full px-2.5 py-1 text-[11px] font-medium"
                    >
                      <Plus size={11} className="mr-1" /> Add to comment
                    </Button>
                  ) : null}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SeverityCell({ severity }: { severity: LineSeverity }) {
  const tone = severityTone(severity);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone.bg} ${tone.text} ${tone.border}`}
    >
      <span className={`size-1.5 rounded-full ${tone.dot}`} />
      {severity}
    </span>
  );
}

// ─── Variants ─────────────────────────────────────────────────────────────

// Platform-fit lanes with their Figma-cased labels (1672:31787).
const PLATFORM_FIT_LANES = [
  { key: 'youtube_longform', label: 'YouTube longform' },
  { key: 'reels', label: 'Reels' },
  { key: 'shorts', label: 'Shorts' },
] as const;

function VariantsTab({ data }: { data: ScriptReviewResult }) {
  const { pick } = useScriptTranslationContext();
  const variants = data.platform_variants ?? {};
  const meta = data.platform_variants_meta;
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <section className="min-w-0">
        <h4 className="m-0 mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <DsSparkle size={14} weight="fill" className="shrink-0 text-violet-500" /> Platform-optimized rewrites
        </h4>
        <div className="flex min-w-0 flex-col gap-4">
          {(['safe', 'bolder', 'shorter'] as const).map((key) => {
            const text = pick(variants[key]);
            if (!text) return null;
            const variantMeta = meta ? meta[key] : undefined;
            const insufficient =
              variantMeta?.creative_direction === VARIANT_INSUFFICIENT_RANGE_SENTINEL;
            return (
              <div
                key={key}
                className="min-w-0 flex flex-col gap-3 rounded-xl border border-border bg-card p-4"
              >
                {insufficient ? (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-caption-regular text-amber-800">
                    <Warning size={14} className="mt-0.5 shrink-0" />
                    <span>
                      Insufficient creative range for this lane — see the <strong>safe</strong>{' '}
                      variant.
                    </span>
                  </div>
                ) : variantMeta ? (
                  <VariantHeaderCard meta={variantMeta} />
                ) : (
                  <span className="block text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                    {key.replace(/_/g, ' ')} variant
                  </span>
                )}
                <div className="prose prose-sm max-w-none m-0 wrap-break-word text-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      {data.platform_fit_edits && (
        <section className="min-w-0">
          <h4 className="m-0 mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Platform fit notes
          </h4>
          <div className="flex min-w-0 flex-col gap-3">
            {PLATFORM_FIT_LANES.map(({ key, label }) => {
              const text = pick(data.platform_fit_edits[key]);
              if (!text) return null;
              return (
                <div key={key} className="min-w-0 rounded-xl border border-border bg-card p-4">
                  <span className="mb-1.5 block text-body-sm-medium text-foreground">{label}</span>
                  <div className="prose prose-sm max-w-none m-0 wrap-break-word text-body-sm-regular text-muted-foreground">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// ─── Safety ───────────────────────────────────────────────────────────────

function SafetyTab({ data }: { data: ScriptReviewResult }) {
  const { pick } = useScriptTranslationContext();
  const checks = data.automated_checks ?? [];
  const issues = data.brand_safety_issues ?? [];
  const coverage = data.brief_coverage ?? [];
  const audit = data.brand_safety_audit;

  return (
    <div className="flex flex-col gap-6">
      {checks.length > 0 && (
        <section>
          <div className="rounded-xl border border-border bg-card p-4 mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span
                className="size-9 shrink-0 rounded-lg"
                style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#A855F7 100%)' }}
                aria-hidden
              />
              <div>
                <span className="text-body-sm-semibold text-foreground">Brand Safety agent recommendation</span>
                <p className="text-caption-regular text-muted-foreground mt-0.5 m-0">
                  This draft has been analyzed against the campaign&apos;s safety protocols.
                </p>
              </div>
            </div>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-caption-medium border border-emerald-200 shrink-0">
              <CheckCircle size={13} /> Reviewed
            </span>
          </div>
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {checks.map((check, i) => (
              <CheckItem
                key={i}
                title={check.title}
                passed={check.passed}
                details={check.details}
                reasoning={check.reasoning}
                recommendation={pick(check.recommendation)}
                isLast={i === checks.length - 1}
              />
            ))}
          </div>
        </section>
      )}

      {/* Brief Coverage (enriched additive) */}
      {coverage.length > 0 ? (
        <section>
          <h4 className="flex items-center gap-2 text-body-sm-semibold text-foreground mb-3 m-0">
            Brief coverage
          </h4>
          <BriefCoverageTable rows={coverage} />
        </section>
      ) : null}

      {/* Brand Safety Issues (always rendered — shows success card when empty) */}
      {issues.length > 0 ? (
        <section className="rounded-xl border border-border bg-card p-5">
          <h4 className="flex items-center gap-2 text-body-sm-semibold text-red-600 mb-4 m-0">
            <Warning size={16} /> Brand safety violations detected
          </h4>
          <div className="flex flex-col gap-4">
            {issues.map((issue, i) => {
              const claim = pick(issue.disallowed_claim_or_competitor) || '(unspecified claim)';
              const fix = pick(issue.recommended_fix);
              return (
                <div key={i} className="bg-red-50 border border-red-100 rounded-xl p-4">
                  <p className="font-semibold text-amber-700 text-body-sm-regular mb-1 m-0">{claim}</p>
                  <p className="text-body-sm-regular text-foreground mb-3 m-0">{String(issue.explanation ?? '')}</p>
                  {fix ? (
                    <span className="inline-flex items-start gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-caption-medium text-emerald-700">
                      <CheckCircle size={13} className="mt-px shrink-0" />
                      <span><span className="font-semibold">Fix:</span> {fix}</span>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : (
        <div className="rounded-xl border border-border bg-card p-8 text-center border-t-4 border-t-emerald-500">
          <span className="flex items-center justify-center gap-2 text-emerald-600 text-body-sm-medium">
            <CheckCircle size={18} /> No Brand Safety Violations Detected
          </span>
        </div>
      )}

      {/* Brand-safety audit (enriched additive — collapsed by default) */}
      {audit ? (
        <details className="rounded-xl border border-border bg-card p-4 text-body-sm-regular">
          <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Brand-safety audit
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            {audit.findings_summary ? (
              <p className="m-0 text-body-sm-regular text-foreground leading-relaxed">{audit.findings_summary}</p>
            ) : null}
            <ChipList label="Checked competitors" items={audit.checked_competitors ?? []} />
            <ChipList
              label="Checked disallowed claims"
              items={audit.checked_disallowed_claims ?? []}
            />
            <ChipList label="Checked disclaimers" items={audit.checked_disclaimers ?? []} />
          </div>
        </details>
      ) : null}
    </div>
  );
}

function ChipList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="m-0 mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground"
          >
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Inline CheckItem (historic) ──────────────────────────────────────────

function CheckItem({
  title,
  passed,
  details,
  reasoning,
  recommendation,
  isLast,
}: {
  title: string;
  passed: boolean;
  details: string;
  reasoning?: string;
  recommendation?: string;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={isLast ? '' : 'border-b border-border'}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex justify-between items-center px-5 py-4 hover:bg-muted text-left rounded-none h-auto"
      >
        <div className="flex items-center gap-3">
          {passed ? (
            <CheckCircle size={17} color="#10b981" className="shrink-0" />
          ) : (
            <XCircle size={17} color="#ef4444" className="shrink-0" />
          )}
          <span className="text-body-sm-medium text-foreground">{title}</span>
        </div>
        {expanded ? (
          <CaretUp size={15} color="#9ca3af" className="shrink-0" />
        ) : (
          <CaretDown size={15} color="#9ca3af" className="shrink-0" />
        )}
      </Button>

      {expanded && (
        <div className="px-5 pb-5 flex flex-col gap-3" style={{ paddingLeft: 52 }}>
          <p className="text-body-sm-regular text-muted-foreground leading-relaxed m-0">{details}</p>
          {reasoning && (
            <div
              className={`bg-muted rounded-r-lg px-4 py-3 border-l-[3px] ${passed ? 'border-emerald-500' : 'border-red-500'}`}
            >
              <span className="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                AI Reasoning
              </span>
              <p className="text-body-sm-regular text-foreground leading-relaxed m-0">{reasoning}</p>
            </div>
          )}
          {!passed && recommendation && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              <div className="flex items-center gap-1.5 text-caption-medium text-red-600 mb-1.5">
                Recommended Fix
              </div>
              <p className="text-body-sm-regular text-red-700 leading-relaxed m-0">{recommendation}</p>
            </div>
          )}
          {passed && recommendation && (
            <p className="text-caption-regular text-muted-foreground italic m-0">Tip: {recommendation}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Ring colour thresholds shared by the Overview hero + secondary donuts
 *  (matches ScoreGauge: ≥75 green, ≥50 amber, else red). */
const donutColor = (s: number): string => (s >= 75 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444');

/** Bare progress ring (no card) — the Overview hero + secondary donuts. */
function Donut({
  score,
  size,
  stroke,
  children,
}: {
  score: number;
  size: number;
  stroke: number;
  children?: ReactNode;
}) {
  const safe = Math.min(100, Math.max(0, score ?? 0));
  const color = donutColor(safe);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - safe / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size }} className="shrink-0">
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f4f4f2" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        {children}
      </div>
    </div>
  );
}

/** Large borderless Overall gauge — left of the Overview hero. */
function HeroGauge({ score }: { score: number }) {
  const safe = Math.min(100, Math.max(0, score ?? 0));
  const color = donutColor(safe);
  return (
    <Donut score={safe} size={118} stroke={9}>
      <span className="flex items-baseline leading-none">
        <span className="text-[22px] font-black tabular-nums" style={{ color }}>{safe}</span>
        <span className="text-[11px] font-semibold text-muted-foreground">/100</span>
      </span>
      <span className="mt-1 text-[11px] font-medium text-muted-foreground">Overall</span>
    </Donut>
  );
}

/** Small donut + label/score for the secondary Brand-fit / Compliance /
 *  Quality row; click-to-expand its pillar breakdown when present. */
function MiniDonut({ title, score, breakdown }: { title: string; score: number; breakdown?: ScoreBreakdown }) {
  const [open, setOpen] = useState(false);
  const safe = Math.min(100, Math.max(0, score ?? 0));
  const color = donutColor(safe);
  const row = (
    <div className="flex items-center gap-3">
      <Donut score={safe} size={46} stroke={5}>
        <span className="text-[12px] font-bold tabular-nums text-foreground">{safe}</span>
      </Donut>
      <div className="flex min-w-0 flex-col text-left">
        <span className="text-[12px] font-medium text-foreground">{title}</span>
        <span className="text-[15px] font-bold leading-tight tabular-nums" style={{ color }}>
          {safe}
          <span className="text-[11px] font-medium text-muted-foreground">/100</span>
        </span>
      </div>
    </div>
  );
  if (!breakdown) return row;
  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="h-auto w-full justify-start p-0 hover:bg-transparent"
        aria-expanded={open}
      >
        {row}
      </Button>
      {open ? (
        <div className="mt-2 rounded-lg border border-border bg-card p-3 text-caption-regular text-foreground">
          {breakdown.reasoning ? (
            <p className="m-0 mb-2 text-[11px] leading-relaxed text-muted-foreground">{breakdown.reasoning}</p>
          ) : null}
          {breakdown.pillars?.length > 0 ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {breakdown.pillars.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="font-semibold text-foreground">
                    {p.name}
                    <span className="ml-1 text-muted-foreground">({p.score}/100)</span>
                  </span>
                  <span className="text-muted-foreground">— &ldquo;{p.evidence}&rdquo;</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Inline Hook / Pacing / CTA sub-score (label over value) in the hero;
 *  click-to-expand the per-metric detail when present. */
function HpcInline({
  label,
  value,
  detail,
}: {
  label: string;
  value: number;
  detail?: Array<{ label: string; value: string }>;
}) {
  const [open, setOpen] = useState(false);
  const inner = (
    <div className="flex flex-col items-start">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-xl font-bold tabular-nums text-foreground">
        {Number.isFinite(value) ? value.toFixed(1) : '—'}
      </span>
    </div>
  );
  if (!detail) return inner;
  return (
    <div className="flex flex-col items-start">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="h-auto p-0 hover:bg-transparent"
        aria-expanded={open}
      >
        {inner}
      </Button>
      {open ? (
        <div className="mt-2 w-64 max-w-full rounded-lg border border-border bg-card p-3 text-left text-caption-regular text-foreground">
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {detail.map((row, i) => (
              <li key={i}>
                <span className="font-semibold text-foreground">{row.label}: </span>
                <span className="text-foreground">{row.value || '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ─── GEO Optimized tab ────────────────────────────────────────────────────────

// Tiled lightning-bolt texture for the GEO Agent banner (Figma 2190:38982).
const GEO_LIGHTNING =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'%3E%3Cpath d='M17 4l-6 10h4l-2 8 7-11h-4z' fill='%23C4B5FD'/%3E%3C/svg%3E\")";

/** GEO section heading. The info glyph is a hover tooltip explaining the section
 *  (dark bubble w/ arrow, Figma text-container); optional right-aligned action. */
function GeoSectionHead({ title, action, hint }: { title: string; action?: ReactNode; hint?: string }) {
  const info = <DsInfo size={13} className="text-muted-foreground" aria-hidden />;
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1.5">
        <h4 className="m-0 text-body-sm-semibold text-foreground">{title}</h4>
        {hint ? (
          <SimpleTooltip content={hint} side="right">
            {/* Radix's TooltipTrigger asChild merges hover/focus handlers onto
                whatever child it's given, but a bare <svg> icon isn't natively
                focusable — that left this hint keyboard/screen-reader
                unreachable (hover-only). A real <button>, matching the
                CreatorAnalysisButton pattern elsewhere in this codebase, is
                focusable by default and gets the tooltip's built-in
                focus-to-show / Escape-to-dismiss behavior for free. */}
            {/* -m-1.5/p-1.5 expands the hit target to ~24x24 (WCAG 2.5.8 Target
                Size Minimum) around the 13px icon without shifting layout —
                the negative margin cancels the padding's effect on flow. */}
            <button
              type="button"
              aria-label={hint}
              className="-m-1.5 inline-flex cursor-help items-center rounded-sm p-1.5 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring"
            >
              {info}
            </button>
          </SimpleTooltip>
        ) : (
          info
        )}
      </div>
      {action}
    </div>
  );
}

/** Enforce the single-select (radio) invariant on title:* keys when seeding a
 *  persisted GEO selection — legacy/bad data could contain two title:* keys, which
 *  would render two title cards as "selected" (incl. the read-only Confirmed view)
 *  until the next toggle. Keep only the first title:* key. */
function radioNormalizeSelection(keys: string[]): Set<string> {
  const set = new Set<string>();
  let hasTitle = false;
  for (const k of keys) {
    if (k.startsWith('title:')) {
      if (hasTitle) continue;
      hasTitle = true;
    }
    set.add(k);
  }
  return set;
}

/** Rounded grey chip (Figma GEO) — used for the Target AI Questions list. */
function GeoChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-[#F4F4F2] px-3 py-1.5 text-[13px] font-medium text-foreground">
      {children}
    </span>
  );
}

// Closed set of non-title GEO pack elements the brand can independently
// include/exclude (titles are handled separately via `title:${i}` keys —
// see radioNormalizeSelection above). Narrowing selBtn()'s key param to this
// union — instead of a plain string — turns a future unmapped key (typo, or
// a new element added without updating SEL_LABEL) into a compile error
// instead of a silent `aria-label={undefined}`.
type GeoSelectableElement = 'description' | 'pinned' | 'cues';

function GeoTab({
  envelope,
  onGenerate,
  generating,
  onRegenerate,
  onSaveSelection,
  readOnly,
}: {
  envelope?: GeoAiEnvelope;
  onGenerate?: () => void | Promise<void>;
  generating?: boolean;
  onRegenerate?: (element: string) => void | Promise<void>;
  onSaveSelection?: (selected: string[]) => void | Promise<void>;
  /** Confirmed/finalized read-only mode — render the approved pack + the
   *  persisted selection (highlighted) with no editing controls. */
  readOnly?: boolean;
}) {
  const status = envelope?.status;
  const pack: GeoPack | undefined = envelope?.data?.result;

  // Translate-to-English: pick native vs translations.en per field, driven by the toggle.
  const { pick } = useScriptTranslationContext();
  const gt = (v?: { text?: string; translations?: Record<string, string> }): string =>
    pick(v as Parameters<typeof pick>[0]);

  // Auto-trigger: when the GEO tab is opened and no pack exists yet, kick off
  // generation once (no manual "Generate" click). The server gate (approved
  // script) + dedup make a stray trigger harmless. Fires a single time per mount.
  // Generation is auto-triggered at the panel level (ScriptResultTabs) when the
  // review opens, so the GeoTab itself only DISPLAYS state — it never triggers.
  const noPackYet = !envelope || (!status && !pack);

  // Per-element selection — the brand marks the titles / description / chapters /
  // pinned / cues they want. Persisted (auto-saved) as the approved/chosen set:
  // seeded from the persisted envelope.selected, and every toggle POSTs the new
  // set via onSaveSelection so it survives reopening.
  const [selected, setSelected] = useState<Set<string>>(() =>
    radioNormalizeSelection(envelope?.selected ?? []),
  );
  // A ref mirror of `selected`, kept current via an effect — read after async
  // work (e.g. a regenerate resolving) without capturing a stale closure value.
  // The effect commits before any post-await continuation runs.
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);
  // The pack (and its persisted selection) can arrive after mount, so seed the
  // Set once when envelope.selected first becomes available.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (envelope?.selected && envelope.selected.length > 0) {
      seededRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time seed from async-arriving envelope data, gated by seededRef (also flipped by user toggles), so it can't loop and can't be derived purely during render
      setSelected(radioNormalizeSelection(envelope.selected));
    }
  }, [envelope?.selected]);
  const toggleSel = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      // Titles are single-select: a video gets exactly one title, so picking one
      // clears any other title:* choice (radio). All other elements (description,
      // chapters, pinned, cues) are independent include/exclude toggles.
      if (key.startsWith('title:')) {
        for (const k of next) if (k.startsWith('title:')) next.delete(k);
      }
      next.add(key);
    }
    seededRef.current = true; // user intent wins over a late seed
    setSelected(next);
    void onSaveSelection?.(Array.from(next));
  };
  // Title Options radiogroup (WAI-ARIA APG Radio Group pattern): roving
  // tabindex means only one card is ever in the Tab order, so Arrow keys MUST
  // move both focus and selection between cards — without this, cards with
  // tabIndex -1 are permanently keyboard-unreachable. titleRefs lets the
  // keydown handler imperatively focus the newly-selected card (tabIndex
  // alone doesn't move DOM focus), mirroring handleTablistKeyDown above.
  const titleRefs = useRef<Partial<Record<number, HTMLDivElement | null>>>({});
  const selectTitleIndex = (i: number) => {
    const key = `title:${i}`;
    if (!selected.has(key)) toggleSel(key);
    titleRefs.current[i]?.focus();
  };
  const handleTitleGroupKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const titles = pack?.titles ?? [];
    const count = titles.length;
    if (!count) return;
    const currentIndex = titles.findIndex((_, i) => selected.has(`title:${i}`));
    const fromIndex = currentIndex === -1 ? 0 : currentIndex;
    let nextIndex: number;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (fromIndex + 1) % count;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (fromIndex - 1 + count) % count;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = count - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    selectTitleIndex(nextIndex);
  };
  // Confirmed / finalized (read-only) view: show ONLY the elements the brand
  // selected — anything not picked is hidden. The editable view shows everything
  // so the brand can choose. (Non-selectable context like Target Questions and
  // Voice Match is always shown.)
  const showEl = (key: string) => !readOnly || selected.has(key);
  // Non-selectable context (Target Questions, Voice Match): in the Confirmed view
  // only surface it once the brand has selected at least one element — nothing
  // selected ⇒ nothing below the banner/verdict.
  const showContext = !readOnly || selected.size > 0;
  const selBtn = (key: GeoSelectableElement) => {
    const on = selected.has(key);
    // Read-only (Confirmed tab): render a static "Selected" badge only on the
    // approved elements — no toggle, no un-selecting the locked pack.
    if (readOnly) {
      return on ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">
          <CheckCircle size={12} className="text-violet-600" /> Selected
        </span>
      ) : null;
    }
    // Select is a review action — always available so the brand can pick the
    // elements they want. The compliance block (e.g. SEBI-prohibited language)
    // is enforced at Approve, not here, so reviewing/selecting is never blocked.
    // Checkbox + "Select" label (Figma 2997:57179): 20px square, 1.5px border,
    // radius-6; checked = brand purple fill + white check. borderColor/bg set
    // inline so tailwind-merge can't drop the arbitrary colors.
    // aria-label disambiguates the checkbox by section — every selBtn() call
    // otherwise announces the identical visible label "Select", so a screen
    // reader user tabbing through Description/Pinned/Cues would hear three
    // indistinguishable "Select, checkbox" announcements.
    const SEL_LABEL: Record<GeoSelectableElement, string> = {
      description: 'Select description',
      pinned: 'Select pinned comment',
      cues: 'Select on-screen cues',
    };
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        aria-label={SEL_LABEL[key]}
        onClick={() => toggleSel(key)}
        // h-7 (28px) matches the sibling regenBtn's height in the same row
        // (Button's h-7) and clears WCAG 2.5.8's 24x24 Target Size Minimum —
        // without it the button's own tappable height was ~20px (the 20px
        // checkbox glyph, no vertical padding), even though the row it sits
        // in was already 28px tall because of its sibling.
        className="flex h-7 items-center gap-2 rounded-md outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring"
      >
        <span
          className="flex size-5 shrink-0 items-center justify-center rounded-[6px] border-[1.5px]"
          style={{ borderColor: on ? '#6C09F7' : '#E5E5E5', backgroundColor: on ? '#6C09F7' : 'transparent' }}
        >
          {on ? <Check size={13} weight="bold" className="text-white" /> : null}
        </span>
        <span className="text-body-sm-medium text-[#737373]">Select</span>
      </button>
    );
  };

  // Per-element regenerate — the brand rejected a value and wants a different
  // one. `element` is '' for the whole pack, else the AI element key. Busy is
  // keyed so only the clicked button spins while the sync request runs; all
  // regenerate buttons disable in-flight to avoid overlapping refetches.
  const [regenBusy, setRegenBusy] = useState<string | null>(null);
  const doRegen = async (element: string) => {
    if (!onRegenerate) return;
    const key = element || 'pack';
    setRegenBusy(key);
    try {
      await onRegenerate(element);
      // A regenerate replaces content with a brand-new, unreviewed value, so any
      // selection pointing at the OLD copy would render in the read-only Confirmed
      // view as if the brand had approved the new copy. Clear the affected
      // selection key(s) once the regenerate resolves, and persist.
      //   whole pack ('')  → clear everything (titles + description + pinned + cues)
      //   'titles'         → clear title:* keys
      //   'description'    → 'description' (also owns the Q&A cards)
      //   'pinned_comment' → 'pinned' ;  'on_screen_cues' → 'cues'
      const affected = (k: string): boolean => {
        if (element === '') return true;
        if (element === 'titles') return k.startsWith('title:');
        if (element === 'description') return k === 'description';
        if (element === 'pinned_comment') return k === 'pinned';
        if (element === 'on_screen_cues') return k === 'cues';
        return false;
      };
      const cur = selectedRef.current;
      if ([...cur].some(affected)) {
        const next = new Set([...cur].filter((k) => !affected(k)));
        setSelected(next);
        void onSaveSelection?.(Array.from(next));
      }
    } finally {
      setRegenBusy(null);
    }
  };
  const regenBtn = (element: string, label = 'Rephrase') => {
    if (readOnly || !onRegenerate) return null;
    const busy = regenBusy === (element || 'pack');
    return (
      <Button
        type="button"
        variant="ghost"
        disabled={regenBusy !== null}
        onClick={() => void doRegen(element)}
        // Figma: purple "Rephrase" with the two-arrow swap icon AFTER the text.
        className="h-7 gap-1.5 px-2 text-xs font-medium text-[#6C09F7] hover:text-[#5A08D0]"
      >
        {busy ? 'Rephrasing…' : label}
        <ArrowsClockwise size={15} className={busy ? 'animate-spin' : ''} />
      </Button>
    );
  };

  // Pack-level regenerate as a solid pill in the banner (Figma 2190:39166):
  // Brand/500 #6C09F7, radius 999, px-14 py-8, gap-8, note icon + 16px Medium
  // white "Regenerate".
  const regenPill = () => {
    if (readOnly || !onRegenerate) return null;
    const busy = regenBusy === 'pack';
    return (
      <Button
        type="button"
        disabled={regenBusy !== null}
        onClick={() => void doRegen('')}
        className="h-auto shrink-0 gap-2 rounded-full bg-[#6C09F7] px-[14px] py-2 text-body-md-medium text-white hover:bg-[#5A08D0]"
      >
        <ArrowsCounterClockwise size={16} className={busy ? 'animate-spin' : ''} />
        {busy ? 'Regenerating…' : 'Regenerate'}
      </Button>
    );
  };

  // Not generated yet. Auto-trigger fires on open (effect above): while the
  // request is in flight we show a spinner; if it couldn't run (e.g. script not
  // approved yet → server 409) we fall back to a manual button so it's never a
  // dead spinner. No generate hook (read-only) → just explain.
  if (noPackYet) {
    if (!onGenerate) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Sparkle size={22} className="text-violet-500" />
          <p className="max-w-sm text-sm text-slate-500">
            No GEO pack yet. AI-citable YouTube metadata (titles, description, chapters,
            pinned comment, on-screen cues) appears here once the script is approved.
          </p>
        </div>
      );
    }
    if (generating) {
      return (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
          <Sparkle size={16} className="animate-pulse text-violet-500" />
          Generating the GEO pack…
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Sparkle size={22} className="animate-pulse text-violet-500" />
        <p className="max-w-sm text-sm text-slate-500">
          Preparing the GEO pack — it generates automatically once the script is
          submitted for review.
        </p>
      </div>
    );
  }

  if (status === 'pending' || status === 'processing') {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
        <Sparkle size={16} className="animate-pulse text-violet-500" />
        Generating the GEO pack… check back in a moment.
      </div>
    );
  }
  if (status === 'failed' || !pack) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-rose-600">
        <XCircle size={20} />
        GEO generation failed{envelope?.error ? `: ${envelope.error}` : '.'}
        {onGenerate ? (
          <Button type="button" variant="ghost" onClick={() => void onGenerate()} disabled={generating} className="gap-2 text-violet-600">
            <Sparkle size={15} /> {generating ? 'Retrying…' : 'Try again'}
          </Button>
        ) : null}
      </div>
    );
  }

  const needs = pack.needs_confirmation ?? [];
  const auth = pack.authenticity;
  const vm = pack.voice_match;
  const verdictHeadline = auth
    ? auth.blocked
      ? 'Not publish-ready — fix the flagged items before using this pack'
      : auth.passed
        ? 'Built to get your video recommended by AI'
        : 'Review the flagged claims before using this pack'
    : null;
  // Verdict body: the descriptive Figma copy when the pack is clean; the actual
  // disclosure/unsourced/notes status when it's blocked or has flagged claims
  // (so the compliance reason is never hidden).
  const verdictBody = auth
    ? auth.passed && !auth.blocked
      ? 'This GEO pack shapes the titles, description, and Q&A so ChatGPT, Gemini, and Perplexity surface it when people ask about your product while staying on-brief, keeping every claim backed by the script.'
      : auth.notes?.length
        ? auth.notes.join('; ')
        : `${auth.unsourced_count ?? 0} claim${(auth.unsourced_count ?? 0) === 1 ? '' : 's'} need sourcing`
    : null;
  // Title Options fallback tab stop when nothing is selected yet: must check
  // title:* selection specifically, NOT `selected.size` — the brand may have
  // already toggled description/pinned/cues, which would make `selected.size
  // > 0` true while no title is selected. Using the whole-selection size here
  // previously meant zero cards ever got tabIndex 0 in that case, dropping
  // the entire radiogroup out of the Tab order.
  const hasTitleSelected = (pack?.titles ?? []).some((_, j) => selected.has(`title:${j}`));

  return (
    <div className="flex flex-col gap-6">
      {/* ── GEO hero card: banner + verdict wrapped in a card (Figma outer
          container — bg-card #FFFFFF, 1px Border/Grey/default #E5E5E5,
          radius-xl, 24px padding + 24px gap) ─────────────────────────────── */}
      <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-6">
      {/* ── GEO Agent banner (Figma 2190:39097) ──────────────────────────── */}
      <div
        className="relative overflow-hidden rounded-[14px] border border-[#DBBEFE] px-5 py-4"
        style={{ background: 'linear-gradient(90deg,#FDFCFE 0%,#F5F1FC 60%,#EAE5F8 100%)' }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 left-1/3"
          style={{
            backgroundImage: GEO_LIGHTNING,
            backgroundSize: '26px 26px',
            opacity: 0.6,
            WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 60%)',
            maskImage: 'linear-gradient(90deg, transparent, #000 60%)',
          }}
        />
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={geoAgentAvatar}
              alt=""
              aria-hidden
              className="size-[52px] shrink-0 rounded-full"
            />
            <div className="min-w-0">
              <div className="text-body-md-semibold text-foreground">GEO Agent</div>
              <div className="text-caption-regular text-[#737373]">
                Don&apos;t like a section? Rephrase it for a different take.
              </div>
            </div>
          </div>
          {/* Regenerate pill sits in the banner (Figma 2190:39166), not below */}
          {regenPill()}
        </div>
      </div>

      {/* ── Verdict (Figma 2190:39098) — headline + body directly under banner ── */}
      {auth ? (
        <div className="flex flex-col gap-2">
          <h3
            className="m-0 text-heading-h6"
            // Color via inline style, NOT a cn() text-* class: tailwind-merge
            // misclassifies the custom `text-heading-h6` token as a text-color
            // and drops the 20px size when any text-* color follows it. Verified
            // headline = #000000 (Figma Heading/H6); blocked/warning keep tones.
            style={{ color: auth.blocked ? '#e11d48' : auth.passed ? '#000000' : '#b45309' }}
          >
            {verdictHeadline}
          </h3>
          <p className="m-0 text-body-md-regular/6 font-normal text-[#404040]">
            {verdictBody}
          </p>
        </div>
      ) : null}
      </div>
      {/* ── end GEO hero card ────────────────────────────────────────────── */}

      {/* Selection summary — which pack elements the brand has picked */}
      {selected.size > 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800">
          <CheckCircle size={15} className="text-violet-600" />
          <span><b>{selected.size}</b> element{selected.size === 1 ? '' : 's'} selected</span>
          {readOnly ? null : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSelected(new Set());
                void onSaveSelection?.([]);
              }}
              className="ml-auto h-6 px-2 text-xs text-violet-700"
            >
              Clear
            </Button>
          )}
        </div>
      ) : null}

      {/* ── Facts needing brand confirmation (dev) ───────────────────────── */}
      {needs.length ? (
        <section className="flex flex-col gap-2.5 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <h4 className="m-0 text-body-sm-semibold text-amber-800">
            Facts needing brand confirmation ({needs.length})
          </h4>
          <ul className="flex flex-col gap-1.5 text-body-sm-regular text-amber-900">
            {needs.map((n, i) => (
              <li key={`${n.where}:${n.claim}:${i}`} className="flex flex-col">
                <span className="font-medium">{n.claim}</span>
                <span className="text-caption-regular opacity-80">
                  {n.where ? `${n.where} — ` : ''}
                  {n.reason}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ── Target AI Questions ──────────────────────────────────────────────
          The high-intent questions this pack aims to win, shown as chips up top. */}
      {pack.target_questions?.length && showContext ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead title="Target AI Questions" hint="The questions people ask AI that this pack aims to win." />
          <div className="flex flex-wrap gap-2">
            {pack.target_questions.map((q, i) => (
              <GeoChip key={`${q}:${i}`}>{q}</GeoChip>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Voice Match ──────────────────────────────────────────────────────
          The voice IS the summary. source_videos are internal provenance (raw
          YouTube IDs) — not brand-facing; show only a count, never the IDs. */}
      {vm && showContext ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead title="Voice Match" hint="Written to sound like the creator, based on their videos." />
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
            {vm.handle || vm.traits?.length ? (
              // Rich layout (Figma 2190:39196): @handle + "Learned from…" + chips.
              <>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {vm.handle ? (
                    <span className="text-body-md-regular/6 font-medium text-[#5A07CE]">@{vm.handle}&apos;s</span>
                  ) : null}
                  <span className="text-body-sm-regular text-[#737373]">
                    Learned from: last {vm.source_videos?.length ?? 0} videos
                    {vm.handle ? ` on @${vm.handle}` : ''}.
                  </span>
                </div>
                {vm.traits?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {vm.traits.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center rounded-full border border-border bg-[#F9F9F7] px-3 py-1 text-body-sm-regular/5 font-medium text-[#737373]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              // Legacy pack (pre-traits) or generic voice — show the summary.
              <p className="m-0 text-body-sm-regular text-foreground">
                {vm.summary || 'Generic style — creator profile unavailable.'}
              </p>
            )}
          </div>
        </section>
      ) : null}

      {/* ── Title Options ────────────────────────────────────────────────── */}
      {pack.titles?.length && (!readOnly || pack.titles.some((_, i) => selected.has(`title:${i}`))) ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead title="Title Options" action={regenBtn('titles')} hint="Titles that match how people search." />
          {!readOnly ? (
            <p className="m-0 -mt-1 text-caption-regular text-muted-foreground">Select one from the top three</p>
          ) : null}
          <div
            className="flex flex-col gap-3"
            role={readOnly ? undefined : 'radiogroup'}
            aria-label={readOnly ? undefined : 'Title options — pick one'}
            onKeyDown={readOnly ? undefined : handleTitleGroupKeyDown}
          >
            {pack.titles.map((t, i) => {
              const key = `title:${i}`;
              // Confirmed view: only render the selected title(s).
              if (readOnly && !selected.has(key)) return null;
              const recommended = !!t.recommended;
              const isSelected = selected.has(key);
              const wins = t.target_questions?.length ? `Wins: ${t.target_questions.join(' · ')}` : t.why;
              // aria-label below replaces the radio's accessible NAME with just the
              // title text (avoids a run-on announcement of title + wins + badge) —
              // but that also makes the "Recommended" badge and wins/reasoning text
              // invisible to screen-reader users, even though sighted users see them.
              // aria-describedby restores that as supplementary DESCRIPTION content
              // (announced after the name, not folded into it) instead of dropping it.
              const recommendedId = recommended ? `geo-title-recommended-${i}` : undefined;
              const winsId = wins ? `geo-title-wins-${i}` : undefined;
              const describedBy = [recommendedId, winsId].filter(Boolean).join(' ') || undefined;
              // Re-activating the already-checked radio (click / Enter / Space on the
              // tabbable card) must be a no-op, not a deselect — toggleSel() unconditionally
              // removes a present key, which native radios never allow. Without this guard,
              // re-pressing Enter on the selected title strands the picker with nothing
              // selected. Mirrors the same guard already in selectTitleIndex (Arrow/Home/End).
              const pick = () => {
                if (!isSelected) toggleSel(key);
              };
              return (
                <div
                  key={t.text?.text ?? i}
                  ref={(el) => {
                    titleRefs.current[i] = el;
                  }}
                  // Single-select → radio semantics. Roving tabindex: the selected
                  // card (or the first, when none selected) is the tab stop; Arrow/
                  // Home/End move both focus and selection (handleTitleGroupKeyDown).
                  role={readOnly ? undefined : 'radio'}
                  tabIndex={readOnly ? undefined : (isSelected || (i === 0 && !hasTitleSelected) ? 0 : -1)}
                  aria-checked={readOnly ? undefined : isSelected}
                  // Accessible name is just the title text — without this it's
                  // computed from ALL visible text in the card (title + "Wins: …"
                  // caption + "Recommended" badge), a confusing run-on announcement.
                  aria-label={readOnly ? undefined : gt(t.text)}
                  // Restores the wins/reasoning + "Recommended" signal as a
                  // description (announced after the name) so it isn't silently
                  // dropped for screen-reader users now that aria-label above
                  // replaces the name instead of deriving it from this content.
                  aria-describedby={readOnly ? undefined : describedBy}
                  onClick={readOnly ? undefined : pick}
                  onKeyDown={
                    readOnly
                      ? undefined
                      : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            pick();
                          }
                        }
                  }
                  className={cn(
                    // Selection = a 1px purple border on the card (Figma); the tick
                    // is the only other indicator. Radius 12 / padding 16 per spec.
                    // borderColor is set inline when selected so tailwind-merge can't
                    // drop the arbitrary border-[#6C09F7] against the base `border`.
                    readOnly ? '' : 'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring',
                    'rounded-xl border border-border p-4 transition-colors',
                    readOnly ? '' : 'cursor-pointer hover:border-[#6C09F7]',
                  )}
                  style={isSelected ? { borderColor: '#6C09F7' } : undefined}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-body-md-semibold text-foreground">{gt(t.text)}</div>
                      {wins ? (
                        <div id={winsId} className="mt-1 text-caption-regular text-muted-foreground">
                          {wins}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2.5">
                      {recommended ? (
                        <span
                          id={recommendedId}
                          className="inline-flex items-center gap-1 rounded-full bg-[rgba(108,9,247,0.10)] px-2 py-0.5 text-[11px] font-medium text-[#6C09F7]"
                        >
                          <DsStar size={11} weight="fill" /> Recommended
                        </span>
                      ) : null}
                      {/* Tick = the selection indicator: filled purple check when
                          chosen, outline circle when not. No Select/Selected pill. */}
                      {isSelected ? (
                        <CheckCircle size={22} weight="fill" className="text-[#6C09F7]" aria-hidden />
                      ) : readOnly ? null : (
                        <Circle size={22} className="text-gray-300" aria-hidden />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* ── Description ──────────────────────────────────────────────────── */}
      {pack.description && showEl('description') ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead
            title="Description"
            hint="Answers the big questions up top, so AI pulls them."
            action={
              <div className="flex items-center gap-1">
                {regenBtn('description')}
                {selBtn('description')}
              </div>
            }
          />
          {/* Body card (Figma 2997:57182): the description body text. */}
          <div className="rounded-xl border border-border bg-[#FAFAFA] p-4">
            <p className="m-0 whitespace-pre-wrap text-body-md-regular text-[#171717]">
              {gt(pack.description.body)}
            </p>
          </div>
        </section>
      ) : null}

      {/* ── Questions people will ask AI (Figma 2997:57193) — sits below the
          Description; Q&A as cards. No own Rephrase/Select: the Q&A is part of
          the `description` element, so it follows the Description's selection. ── */}
      {pack.description?.qa_blocks?.length && showEl('description') ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead
            title="Questions people will ask AI"
            hint="What people ask ChatGPT, Gemini and Perplexity about this topic."
          />
          <div className="flex flex-col gap-2">
            {pack.description.qa_blocks.map((qa, i) => (
              <div key={qa.target_question ?? qa.question ?? i} className="flex flex-col">
                {/* Question — grey header panel, rounded top */}
                <div className="rounded-t-xl border border-border bg-[#FAFAFA] px-4 py-2.5">
                  <p className="m-0 text-body-sm-medium text-[#171717]">
                    Q.{i + 1} {qa.question}
                  </p>
                </div>
                {/* Answer — white panel, rounded bottom */}
                <div className="rounded-b-xl border-x border-b border-border bg-card px-4 py-2">
                  <p className="m-0 text-body-sm-regular text-[#404040]">Ans: {gt(qa.answer)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Chapters are intentionally NOT shown here — they need REAL timestamps
          from the shot video, so they're finalized on the Content Review side
          (GeoFinalizeTab), not in the pre-video script pack. */}

      {/* ── Pinned Comment ───────────────────────────────────────────────── */}
      {pack.pinned_comment && showEl('pinned') ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead
            title="Pinned Comment"
            hint="A quick summary AI trusts, because the creator posted it."
            action={
              <div className="flex items-center gap-1">
                {regenBtn('pinned_comment')}
                {selBtn('pinned')}
              </div>
            }
          />
          {/* Pinned comment box (Figma 2190:39275): light-grey fill, 14px Medium. */}
          <div className="whitespace-pre-wrap rounded-xl border border-border bg-[#FAFAFA] p-4 text-body-sm-regular font-medium text-foreground">
            {gt(pack.pinned_comment.body)}
          </div>
        </section>
      ) : null}

      {/* ── On-screen Text Cues ──────────────────────────────────────────── */}
      {pack.on_screen_cues?.length && showEl('cues') ? (
        <section className="flex flex-col gap-3">
          <GeoSectionHead
            title="On-screen Text Cues"
            hint="On-screen text AI can read and quote back later."
            action={
              <div className="flex items-center gap-1">
                {regenBtn('on_screen_cues')}
                {selBtn('cues')}
              </div>
            }
          />
          {/* Figma On-screen Cues: just timestamp + the short cue line. */}
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
            {pack.on_screen_cues.map((cue, i) => (
              <div key={`${cue.timestamp}:${i}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-10 shrink-0 text-body-sm-semibold tabular-nums text-[#6C09F7]">{cue.timestamp}</span>
                <span className="min-w-0 flex-1 text-body-sm-regular text-foreground">{gt(cue.text)}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
